// 增强的ICOPE评估结果页面 - 基于三层建议池系统
import React, { useState, useMemo, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { apiUserService } from '../../services/apiUserService';
import {
  Heart, BookOpen, Brain, Activity, Apple, Eye, Ear, Smile, Users,
  AlertTriangle, CheckCircle, Clock, ArrowRight, TrendingUp, Shield,
  FileText, Phone, Calendar, X, User, ArrowLeft
} from 'lucide-react';
import { ComprehensiveAssessmentResult } from '../../types/index';
import { generatePersonalizedContent } from '../../services/contentGeneration';
import SpecificAdviceCard from '../common/SpecificAdviceCard'; // 确保导入
import NextActionsPanel from '../common/NextActionsPanel';
import CareBotActivationButton from '../carebot/CareBotActivationButton';
import CognitionTestResult from '../assessment/CognitionTestResult';
import HearingTestResult from '../assessment/HearingTestResult';
import VisionTestResult from '../assessment/VisionTestResult';
import MobilityTestResult from '../assessment/MobilityTestResult';
import NutritionTestResult from '../assessment/NutritionTestResult';
import PsychologicalTestResult from '../assessment/PsychologicalTestResult';
import SocialCareTestResult from '../assessment/SocialCareTestResult';
import UrinaryTestResult from '../assessment/UrinaryTestResult';
import CarerSupportTestResult from '../assessment/CarerSupportTestResult';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import MobilitySummaryDisplay from '../assessment/summary/MobilitySummaryDisplay';
import NutritionSummaryDisplay from '../assessment/summary/NutritionSummaryDisplay';
import VisionSummaryDisplay from '../assessment/summary/VisionSummaryDisplay';
import CognitionSummaryDisplay from '../assessment/summary/CognitionSummaryDisplay';
import HearingSummaryDisplay from '../assessment/summary/HearingSummaryDisplay';
import PsychologicalSummaryDisplay from '../assessment/summary/PsychologicalSummaryDisplay';

interface EnhancedResultsPageProps {
  assessmentResult: ComprehensiveAssessmentResult | null;
}

const EnhancedResultsPage: React.FC<EnhancedResultsPageProps> = ({ assessmentResult }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { assessmentId: urlAssessmentId } = useParams<{ assessmentId: string }>();
  // urlUserId 已从路由中移除，保留变量名兼容下方既有逻辑（统一回退到 currentUser/assessment 自带的 userId）
  const urlUserId: string | undefined = undefined;
  const { carer } = useAuth();
  const { currentUser, userState, refreshUser, switchUser, getUserDisplayName, getUserAgeRange, isUserLoaded } = useUser();
  const toast = useToast();

  // UserContext 自动处理用户状态管理

  // 从服务器加载评估数据的状态
  const [loadedAssessmentResult, setLoadedAssessmentResult] = useState<ComprehensiveAssessmentResult | null>(null);
  const [loadingAssessment, setLoadingAssessment] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // 实际使用的评估结果：优先使用prop传入的，否则使用从服务器加载的
  const activeAssessmentResult = assessmentResult || loadedAssessmentResult;

  // 🎯 统一的用户信息获取 - 支持临时评估和正式评估两种模式
  const userDisplayInfo = useMemo(() => {
    // 模式1：临时评估 - 优先使用state传递的userProfile（不依赖UserContext）
    const stateUserProfile = location.state?.userProfile;
    if (stateUserProfile) {
      console.log('📋 [EnhancedResultsPage] 使用临时评估用户信息:', stateUserProfile.name);
      return {
        name: stateUserProfile.name || '用户',
        age: stateUserProfile.age || null,
        gender: stateUserProfile.gender || null,
        ageRange: stateUserProfile.ageRange || 'unknown',
        assessmentFor: stateUserProfile.assessmentFor || 'self',
        preferredContentLevel: stateUserProfile.preferredContentLevel || 'standard',
        source: 'state-temporary' as const
      };
    }

    // 模式2：正式评估 - 使用UserContext的currentUser
    // ✅ UserProfile的属性是直接在currentUser上，不需要.personalInfo嵌套
    if (currentUser && currentUser.name) {
      console.log('🌐 [EnhancedResultsPage] 使用API用户信息:', currentUser.name);
      return {
        name: currentUser.name || '用户',
        age: currentUser.age || null,
        gender: currentUser.gender || null,
        ageRange: currentUser.ageRange || 'unknown',
        assessmentFor: currentUser.assessmentFor || 'self',
        preferredContentLevel: currentUser.preferredContentLevel || 'standard',
        source: 'context-api' as const
      };
    }

    // ✅ 模式3：从评估结果中获取用户ID，然后触发加载
    // 这是一个临时方案，等UserContext加载完成后会切换到模式2
    if (activeAssessmentResult?.userId) {
      console.log('🔄 [EnhancedResultsPage] 从评估结果中找到userId，等待UserContext加载:', activeAssessmentResult.userId);
      return {
        name: '正在加载...',
        age: null,
        gender: null,
        ageRange: 'unknown' as const,
        assessmentFor: 'self' as const,
        preferredContentLevel: 'standard' as const,
        source: 'loading' as const
      };
    }

    // 模式4：兜底默认值
    console.warn('⚠️ [EnhancedResultsPage] 无用户信息，使用默认值');
    return {
      name: '用户',
      age: null,
      gender: null,
      ageRange: 'unknown' as const,
      assessmentFor: 'self' as const,
      preferredContentLevel: 'standard' as const,
      source: 'default' as const
    };
  }, [location.state, currentUser, activeAssessmentResult]);

  const [activeTab, setActiveTab] = useState<'summary' | 'universal' | 'specific'>('summary');
  const [modalContent, setModalContent] = useState<{title: string, items: any[]} | null>(null);

  // 使用 state 来管理建议列表，以便实现排序
  const [specificAdviceList, setSpecificAdviceList] = useState<any[]>([]);

  // 🔄 从URL参数加载评估数据
  useEffect(() => {
    const loadAssessmentFromUrl = async () => {
      // 如果已经有prop传入的评估结果，或者没有URL参数，则跳过
      if (assessmentResult || !urlAssessmentId) {
        return;
      }

      try {
        setLoadingAssessment(true);
        setLoadError(null);
        console.log(`🔄 [EnhancedResultsPage] 从服务器加载评估数据: ${urlAssessmentId}`);

        const assessmentRecord = await apiUserService.getAssessmentById(urlAssessmentId);

        // 转换AssessmentRecord到ComprehensiveAssessmentResult格式
        if (assessmentRecord && assessmentRecord.result) {
          // ✅ 修复：将userId和assessmentDate都包含到result中
          const resultWithUserId = {
            ...assessmentRecord.result,
            userId: assessmentRecord.userId,  // ← 添加userId
            completedAt: assessmentRecord.assessmentDate  // ← 添加评估日期
          };
          setLoadedAssessmentResult(resultWithUserId);
          console.log('✅ [EnhancedResultsPage] 评估数据加载成功，userId:', assessmentRecord.userId);
          console.log('📊 [EnhancedResultsPage] 评估数据结构:', {
            assessmentId: assessmentRecord.id,
            hasUserId: !!assessmentRecord.userId,
            hasResult: !!assessmentRecord.result,
            resultKeys: assessmentRecord.result ? Object.keys(assessmentRecord.result) : 'no-result',
            intrinsicCapacityKeys: assessmentRecord.result?.intrinsicCapacity ? Object.keys(assessmentRecord.result.intrinsicCapacity) : 'no-intrinsic'
          });

          // 如果有用户ID，切换到该用户
          if (urlUserId || assessmentRecord.userId) {
            refreshUser(urlUserId || assessmentRecord.userId);
          }
        } else {
          console.error('❌ [EnhancedResultsPage] 评估记录格式无效:', {
            assessmentRecord,
            hasAssessmentRecord: !!assessmentRecord,
            hasResult: assessmentRecord?.result,
            assessmentKeys: assessmentRecord ? Object.keys(assessmentRecord) : 'no-assessment'
          });
          throw new Error('评估记录格式无效 - 缺少result字段');
        }
      } catch (error) {
        console.error('❌ [EnhancedResultsPage] 加载评估数据失败:', error);
        setLoadError(error instanceof Error ? error.message : '加载评估数据失败');
      } finally {
        setLoadingAssessment(false);
      }
    };

    loadAssessmentFromUrl();
  }, [urlAssessmentId, urlUserId, assessmentResult, refreshUser]);

  // 如果有评估结果中的用户ID，尝试切换到该用户
  useEffect(() => {
    if (assessmentResult?.userId && (!currentUser || currentUser.id !== assessmentResult.userId)) {
      refreshUser(assessmentResult.userId);
    }
  }, [assessmentResult?.userId, currentUser?.id, refreshUser]);

  // 🔐 验证userId存在性 - 防止刷新时丢失用户上下文
  useEffect(() => {
    const userId = sessionStorage.getItem('currentUserId') ||
                   localStorage.getItem('selectedUserId');
    const hasUserInState = location.state?.userProfile;

    if (!userId && !hasUserInState && !assessmentResult?.userId) {
      console.warn('⚠️ [EnhancedResultsPage] 没有有效的用户ID，重定向到用户选择页面');
      navigate('/user-selection', { replace: true });
    }
  }, [location, navigate, assessmentResult?.userId]);

  // 导航处理函数
  const handleGoBack = async () => {
    // 获取当前用户ID，优先级：URL参数 > UserContext > 评估结果
    const userId = urlUserId || currentUser?.id || activeAssessmentResult?.userId;

    if (userId) {
      // 切换到目标用户后跳转到档案页面
      if (userId !== currentUser?.id) {
        await switchUser(userId);
      }
      navigate('/profile');
    } else {
      // 如果无法获取用户ID，跳转到用户列表
      navigate('/user-selection');
    }
  };

  // 域名到路由的映射
  const getDomainRoute = (domainKey: string) => {
    const routeMapping: { [key: string]: string } = {
      'cognition': '/assessment/cognition',
      'mobility': '/assessment/mobility',
      'vitality': '/assessment/vitality',
      'vision': '/assessment/vision',
      'hearing': '/assessment/hearing',
      'psychologicalCapacity': '/assessment/psychological',
      'socialCareSupport': '/assessment/social-care',
      'carerSupport': '/assessment/carer-support',
      'urinaryIncontinence': '/assessment/urinary-incontinence'
    };
    return routeMapping[domainKey] || '/assessment';
  };

  // 测试详情状态
  const [selectedDomainDetails, setSelectedDomainDetails] = useState<string | null>(null);

  // 处理测试项点击 - 显示详情模态框
  const handleTestClick = (domainKey: string) => {
    setSelectedDomainDetails(domainKey);
  };

  // 关闭详情模态框
  const closeDetailsModal = () => {
    setSelectedDomainDetails(null);
  };

  // 将测试名称转换为中文题干
  const getChineseTestName = (testName: string): string => {
    const nameMap: { [key: string]: string } = {
      // 认知能力
      '三词记忆测试': '三词记忆测试',
      '时间定向-year': '今年是哪一年？',
      '时间定向-month': '现在是几月？', 
      '时间定向-date': '今天是几号？',
      '时间定向-weekday': '今天是星期几？',
      '空间定向-city': '我们现在在哪个城市？',
      '空间定向-place': '我们现在在什么地方？',
      '空间定向-area': '我们在这个地方的哪个区域？',
      '空间定向-transport': '您是怎么来到这里的？',
      
      // 行动能力
      '椅子站立测试': '椅子站立测试',
      'mobility test': '行动能力测试',
      'chair stand test': '椅子站立测试',
      
      // 视力健康
      '视力问题筛查': '视力筛查问题',
      '右眼大E字符测试 (6/60)': '右眼大E字符测试',
      '左眼大E字符测试 (6/60)': '左眼大E字符测试',
      '右眼中号E字符测试 (6/12)': '右眼中号E字符测试',
      '左眼中号E字符测试 (6/12)': '左眼中号E字符测试',
      '近视力测试 (N6)': '近视力测试',
      '外部眼检': '外部眼检',
      'vision test': '视力测试',
      
      // 听力健康
      '听力问题筛查': '听力筛查问题',
      '左耳耳语测试': '左耳耳语测试',
      '右耳耳语测试': '右耳耳语测试',
      '听力自我评估': '听力自我评估',
      'hearing test': '听力测试',
      'whisper test': '耳语测试',
      
      // 心理健康
      '抑郁情绪评估': '抑郁情绪评估',
      'psychological test': '心理健康评估',
      'depression screening': '抑郁症筛查',
      'phq-2': 'PHQ-2心理健康评估',
      
      // 营养状况
      '营养筛查': '营养筛查',
      '营养状况筛查': '营养状况筛查',
      'nutrition test': '营养状况评估',
      'vitality test': '营养活力评估',
      
      // 社会照护
      '社会照护评估': '社会照护评估',
      'social care assessment': '社会照护支持评估',
      
      // 膀胱健康
      '膀胱健康评估': '膀胱健康评估',
      '尿失禁评估': '尿失禁评估',
      'urinary incontinence': '尿失禁评估',
      'bladder health': '膀胱健康评估',
      
      // 照护者支持
      '照护者支持评估': '照护者支持评估',
      'carer support assessment': '照护者支持评估'
    };
    
    return nameMap[testName] || testName;
  };

  // 获取领域的筛查问题（只有认知、视力、听力有筛查问题）
  const getFilterQuestion = (domainKey: string): string => {
    const filterQuestions: { [key: string]: string } = {
      'cognition': '您有记忆或定向方面的问题吗？',
      'vision': '您在看远处或近处的物体时有困难吗？',
      'hearing': '您在听对话时有困难吗？'
    };

    return filterQuestions[domainKey] || '';
  };


  // 获取筛查问题的用户回答描述
  const getFilterAnswerText = (domainKey: string, filterResult: boolean | undefined): string => {
    // 🎯 关键修复：不同评估域的filterQuestionResult语义不同
    if (domainKey === 'cognition') {
      // 认知评估：filterQuestionResult: true = 筛查通过(无困难), false = 筛查失败(有困难)
      if (filterResult === true) {
        return '没有困难'; // 筛查通过
      } else if (filterResult === false) {
        return '有困难'; // 筛查失败
      }
    } else {
      // 听力/视力评估：filterQuestionResult: false = 筛查通过(无困难), true = 筛查失败(有困难)
      if (filterResult === false) {
        return '没有困难'; // 筛查通过
      } else if (filterResult === true) {
        return '有困难'; // 筛查失败
      }
    }
    
    // undefined情况的特殊处理
    if (domainKey === 'hearing') {
      // 听力：如果有耳语测试数据，推断为"没有困难"
      const domainResult = assessmentResult?.intrinsicCapacity?.hearing;
      const hasWhisperTest = domainResult?.testResults?.some((test: any) => 
        test.name === "左耳耳语测试" || test.name === "右耳耳语测试" || test.name === "耳语测试"
      );
      return hasWhisperTest ? '没有困难' : '数据缺失';
    }
    return '数据缺失';
  };

  // 判断筛查问题是否通过 - 统一标准逻辑
  const getFilterPassed = (domainKey: string, filterResult: boolean | undefined): boolean => {
    // 🎯 统一标准：filterQuestionResult的语义应该一致
    // filterQuestionResult: true = 筛查通过(无困难), false = 筛查失败(有困难)
    if (filterResult === true) {
      return true; // 筛查通过（无困难）
    } else if (filterResult === false) {
      return false; // 筛查失败（有困难）
    }
    
    // undefined情况的特殊处理
    if (domainKey === 'hearing') {
      // 听力：如果有耳语测试数据，推断为通过
      const domainResult = assessmentResult?.intrinsicCapacity?.hearing;
      const hasWhisperTest = domainResult?.testResults?.some((test: any) => 
        test.name === "左耳耳语测试" || test.name === "右耳耳语测试" || test.name === "耳语测试"
      );
      return hasWhisperTest;
    }
    return true; // 其他情况默认通过（容错处理）
  };

  // 关键因素字段中文化映射
  const getKeyFactorFieldName = (fieldKey: string, domainKey: string): string => {
    const fieldMappings: { [key: string]: { [key: string]: string } } = {
      // 社会照护支持字段
      socialCareSupport: {
        'home_environment': '居住环境安全',
        'financial_situation': '经济状况',
        'social_isolation': '社会孤立',
        'social_engagement': '社会参与'
      },
      // 尿失禁字段
      urinaryIncontinence: {
        'status': '整体状况',
        'hasProblem': '症状表现',
        'recommendations': '建议措施',
        'severity': '严重程度',
        'frequency': '发生频率'
      },
      // 照护者支持字段
      carerSupport: {
        'supportNeedsMet': '支持需求满足度',
        'hasConfidence': '照护信心水平',
        'negativeImpacts': '负面影响评估',
        'physical': '身体负担',
        'mental': '心理负担',
        'social': '社交影响',
        'financial': '经济负担'
      }
    };

    const domainMappings = fieldMappings[domainKey];
    if (domainMappings && domainMappings[fieldKey]) {
      return domainMappings[fieldKey];
    }

    // 通用字段映射
    const commonMappings: { [key: string]: string } = {
      'status': '评估状态',
      'hasProblem': '问题识别',
      'recommendations': '专业建议',
      'severity': '严重程度',
      'responses': '响应数据'
    };

    return commonMappings[fieldKey] || fieldKey;
  };

  // 获取关键因素字段的状态描述
  const getKeyFactorFieldStatus = (fieldKey: string, value: any, domainKey: string): string => {
    // 社会照护支持的特殊处理
    if (domainKey === 'socialCareSupport') {
      return value ? '有风险' : '正常';
    }

    // 尿失禁的特殊处理
    if (domainKey === 'urinaryIncontinence') {
      if (fieldKey === 'hasProblem') {
        return value ? '存在症状' : '无症状';
      }
      if (fieldKey === 'status') {
        return value === 'failed' ? '需要关注' : '状况良好';
      }
      if (fieldKey === 'recommendations') {
        return value ? '需要采取措施' : '暂无特殊建议';
      }
    }

    // 照护者支持的特殊处理
    if (domainKey === 'carerSupport') {
      if (fieldKey === 'supportNeedsMet') {
        return value ? '需求已满足' : '需求未满足';
      }
      if (fieldKey === 'hasConfidence') {
        return value ? '有信心' : '缺乏信心';
      }
    }

    // 通用布尔值处理
    if (typeof value === 'boolean') {
      return value ? '是' : '否';
    }

    // 字符串和数字直接显示
    return String(value);
  };

  // 获取测试项的完整题干和用户答案 - 支持双数据源（真实测试 vs 模拟数据）
  const getTestQuestionAndAnswer = (testName: string, details: any, responses: any) => {
    // 调试：打印响应数据
    if (testName.includes('时间定向') && responses) {
    }
    
    // 根据测试名称返回题干和答案
    switch (testName) {
      case '时间定向':
        // 🎯 双数据源逻辑：优先使用真实用户数据，降级使用模拟数据
        const userTimeAnswers = responses?.cognitiveTests?.timeOrientation?.userInputs;
        const rawAnswers = details?.rawUserAnswers?.answers; // 从TestResult中的真实答案
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        const currentDate = new Date().getDate();
        const weekdays = ['日', '一', '二', '三', '四', '五', '六'];
        const currentWeekday = weekdays[new Date().getDay()];
        
        // 数据源1：真实用户测试数据（来自TestResult.rawUserAnswers）
        if (rawAnswers) {
          return {
            question: '时间定向测试：请回答当前的年、月、日、星期',
            answer: `年份: ${rawAnswers.year || '未回答'}, ` +
                   `月份: ${rawAnswers.month || '未回答'}, ` +
                   `日期: ${rawAnswers.date || '未回答'}, ` +
                   `星期: ${rawAnswers.weekday || '未回答'}`
          };
        }
        
        // 数据源2：响应数据（来自responses.cognitiveTests）
        if (userTimeAnswers) {
          return {
            question: '时间定向测试：请回答当前的年、月、日、星期',
            answer: `年份: ${userTimeAnswers.year || '未回答'}, ` +
                   `月份: ${userTimeAnswers.month || '未回答'}, ` +
                   `日期: ${userTimeAnswers.date || '未回答'}, ` +
                   `星期: ${userTimeAnswers.weekday || '未回答'}`
          };
        }
        
        // 数据源3：模拟/降级数据
        return {
          question: '时间定向测试：请回答当前的年、月、日、星期',
          answer: responses?.timeOrientation || responses?.timeResults ? 
            `年份: ${currentYear}, 月份: ${currentMonth}, 日期: ${currentDate}, 星期: 星期${currentWeekday}` :
            '用户完成了时间定向测试'
        };

      case '空间定向':
        // 🎯 双数据源逻辑：优先使用真实用户数据，降级使用模拟数据
        const userSpaceAnswers = responses?.cognitiveTests?.spaceOrientation?.userInputs;
        const rawSpaceAnswers = details?.rawUserAnswers?.answers; // 从TestResult中的真实答案
        
        // 数据源1：真实用户测试数据（来自TestResult.rawUserAnswers）
        if (rawSpaceAnswers) {
          return {
            question: '空间定向测试：请回答当前所在的城市、地点、区域及如何到达',
            answer: `城市: ${rawSpaceAnswers.city || '未回答'}, ` +
                   `地点: ${rawSpaceAnswers.place || '未回答'}, ` +
                   `区域: ${rawSpaceAnswers.area || '未回答'}, ` +
                   `交通方式: ${rawSpaceAnswers.transport || '未回答'}`
          };
        }
        
        // 数据源2：响应数据（来自responses.cognitiveTests）
        if (userSpaceAnswers) {
          return {
            question: '空间定向测试：请回答当前所在的城市、地点、区域及如何到达',
            answer: `城市: ${userSpaceAnswers.city || '未回答'}, ` +
                   `地点: ${userSpaceAnswers.place || '未回答'}, ` +
                   `区域: ${userSpaceAnswers.area || '未回答'}, ` +
                   `交通方式: ${userSpaceAnswers.transport || '未回答'}`
          };
        }
        
        // 数据源3：模拟/降级数据
        return {
          question: '空间定向测试：请回答当前所在的城市、地点、区域及如何到达',
          answer: responses?.spaceOrientation || responses?.spaceResults ? 
            `城市: ${responses.spaceOrientation?.city || responses.spaceResults?.city || '未回答'}, ` +
            `地点: ${responses.spaceOrientation?.place || responses.spaceResults?.place || '未回答'}, ` +
            `区域: ${responses.spaceOrientation?.area || responses.spaceResults?.area || '未回答'}, ` +
            `交通方式: ${responses.spaceOrientation?.transport || responses.spaceResults?.transport || '未回答'}` :
            '用户完成了空间定向测试'
        };

      case '三词记忆':
      case '三词记忆测试':
        // 🎯 双数据源逻辑：优先使用真实用户数据，降级使用模拟数据
        const targetWords = ['花', '门', '米饭'];
        const rawWordAnswers = details?.rawUserAnswers?.answers; // 从TestResult中的真实答案
        const selectedWords = responses?.wordRecall?.selectedWords || [];
        
        // 数据源1：真实用户测试数据（来自TestResult.rawUserAnswers）
        if (rawWordAnswers?.selectedWords) {
          return {
            question: `三词记忆测试：请记住并回忆以下三个词汇：${targetWords.join('、')}`,
            answer: rawWordAnswers.selectedWords.length > 0 ? 
              rawWordAnswers.selectedWords.join('、') :
              '无答案'
          };
        }
        
        // 数据源2：响应数据（来自responses.wordRecall）
        if (selectedWords.length > 0) {
          return {
            question: `三词记忆测试：请记住并回忆以下三个词汇：${targetWords.join('、')}`,
            answer: selectedWords.join('、')
          };
        }
        
        // 数据源3：模拟/降级数据
        return {
          question: `三词记忆测试：请记住并回忆以下三个词汇：${targetWords.join('、')}`,
          answer: '无答案'
        };

      case '时间定向-year':
        const yearCorrect = responses?.timeOrientation?.year || responses?.timeResults?.year;
        const yearNow = new Date().getFullYear();
        return {
          question: '今年是哪一年？',
          answer: responses?.userAnswers?.year || 
                 responses?.actualAnswers?.year || 
                 responses?.cognitiveTests?.timeOrientation?.userInputs?.year ||
                 (yearCorrect ? `${yearNow}` : '答案不正确')
        };

      case '时间定向-month':
        const monthCorrect = responses?.timeOrientation?.month || responses?.timeResults?.month;
        const monthNow = new Date().getMonth() + 1;
        return {
          question: '现在是几月？',
          answer: responses?.userAnswers?.month || 
                 responses?.actualAnswers?.month || 
                 responses?.cognitiveTests?.timeOrientation?.userInputs?.month ||
                 (monthCorrect ? `${monthNow}` : '答案不正确')
        };

      case '时间定向-date':
        const dateCorrect = responses?.timeOrientation?.date || responses?.timeResults?.date;
        const dateNow = new Date().getDate();
        return {
          question: '今天是几号？',
          answer: responses?.userAnswers?.date || 
                 responses?.actualAnswers?.date || 
                 responses?.cognitiveTests?.timeOrientation?.userInputs?.date ||
                 (dateCorrect ? `${dateNow}` : '答案不正确')
        };

      case '时间定向-weekday':
        const weekdayCorrect = responses?.timeOrientation?.weekday || responses?.timeResults?.weekday;
        const weekdaysArray = ['日', '一', '二', '三', '四', '五', '六'];
        const weekdayNow = weekdaysArray[new Date().getDay()];
        return {
          question: '今天是星期几？',
          answer: responses?.userAnswers?.weekday || 
                 responses?.actualAnswers?.weekday || 
                 responses?.cognitiveTests?.timeOrientation?.userInputs?.weekday ||
                 (weekdayCorrect ? `星期${weekdayNow}` : '答案不正确')
        };

      case '空间定向-city':
        const cityAnswer = responses?.spaceOrientation?.city || responses?.spaceResults?.city || 
                          responses?.userAnswers?.city || responses?.actualAnswers?.city ||
                          responses?.cognitiveTests?.spaceOrientation?.userInputs?.city;
        return {
          question: '我们现在在哪个城市？',
          answer: cityAnswer || '无答案'
        };

      case '空间定向-place':
        const placeAnswer = responses?.spaceOrientation?.place || responses?.spaceResults?.place || 
                           responses?.userAnswers?.place || responses?.actualAnswers?.place ||
                           responses?.cognitiveTests?.spaceOrientation?.userInputs?.place;
        return {
          question: '我们现在在什么地方？',
          answer: placeAnswer || '无答案'
        };

      case '空间定向-area':
        const areaAnswer = responses?.spaceOrientation?.area || responses?.spaceResults?.area || 
                          responses?.userAnswers?.area || responses?.actualAnswers?.area ||
                          responses?.cognitiveTests?.spaceOrientation?.userInputs?.area;
        return {
          question: '我们在这个地方的哪个区域？',
          answer: areaAnswer || '无答案'
        };

      case '空间定向-transport':
        const transportAnswer = responses?.spaceOrientation?.transport || responses?.spaceResults?.transport || 
                               responses?.userAnswers?.transport || responses?.actualAnswers?.transport ||
                               responses?.cognitiveTests?.spaceOrientation?.userInputs?.transport;
        return {
          question: '您是怎么来到这里的？',
          answer: transportAnswer || '无答案'
        };

      // 行动能力评估
      case '椅子站立测试':
        const completedRises = details?.completedRises || responses?.completedRises || 0;
        return {
          question: '椅子站立测试：请在不使用手臂支撑的情况下，尽可能快速地从椅子上站起坐下5次',
          answer: `用户完成了${completedRises}次站立，${details ? (typeof details.passed !== 'undefined' ? (details.passed ? '测试通过' : '测试未通过') : '测试完成') : '测试完成'}`
        };

      // 视力健康评估
      case '视力问题筛查':
        return {
          question: '视力筛查问题：您在看远处或近处的物体时有困难吗？',
          answer: details?.includes('无视力问题') ? '用户回答：没有困难' : 
                 details?.includes('存在问题') ? '用户回答：有困难' :
                 '用户完成了视力筛查'
        };

      case '右眼大E字符测试 (6/60)':
      case '左眼大E字符测试 (6/60)':
        const eyeSide = testName.includes('右眼') ? '右眼' : '左眼';
        const largeEDetails = details || '';
        return {
          question: `${eyeSide}大E字符测试：请用${eyeSide}识别6米外大E字符的方向（上下左右）`,
          answer: largeEDetails || `用户完成了${eyeSide}大E字符测试`
        };

      case '右眼中号E字符测试 (6/12)':
      case '左眼中号E字符测试 (6/12)':
        const mediumEyeSide = testName.includes('右眼') ? '右眼' : '左眼';
        const mediumEDetails = details || '';
        return {
          question: `${mediumEyeSide}中号E字符测试：请用${mediumEyeSide}识别6米外中号E字符的方向`,
          answer: mediumEDetails || `用户完成了${mediumEyeSide}中号E字符测试`
        };

      case '近视力测试 (N6)':
        return {
          question: '近视力测试：请在40厘米距离识别小号E字符的方向',
          answer: details || '用户完成了近视力测试'
        };

      case '外部眼检':
        return {
          question: '外部眼检：检查眼部是否有明显的异常情况',
          answer: details || '完成了外部眼检'
        };

      // 听力健康评估
      case '听力问题筛查':
        return {
          question: '听力筛查问题：您在听对话时有困难吗？',
          answer: details?.includes('无听力问题') ? '用户回答：没有困难' : 
                 details?.includes('存在听力问题') ? '用户回答：有困难' :
                 '用户完成了听力筛查'
        };

      case '左耳耳语测试':
      case '右耳耳语测试':
        const earSide = testName.includes('左耳') ? '左耳' : '右耳';
        return {
          question: `${earSide}耳语测试：测试者在${earSide}后方60厘米处轻声说话，测试听力`,
          answer: details || `用户完成了${earSide}耳语测试`
        };

      case '听力自我评估':
        return {
          question: '听力自我评估：请评估您的听力状况',
          answer: details || '用户完成了听力自我评估'
        };

      // 心理健康评估
      case '抑郁情绪评估':
        return {
          question: 'PHQ-2抑郁情绪评估：在过去两周内，您有多少天感到沮丧、抑郁或绝望？',
          answer: details || '用户完成了抑郁情绪评估'
        };

      // 营养状况评估
      case '营养筛查':
      case '营养状况筛查':
        return {
          question: '营养筛查：您最近是否出现意外的体重下降？',
          answer: details || '用户完成了营养状况筛查'
        };

      // 社会照护评估
      case '社会照护评估':
        return {
          question: '社会照护评估：评估您在日常生活中获得帮助和支持的情况',
          answer: details || '用户完成了社会照护评估'
        };

      // 膀胱健康评估
      case '膀胱健康评估':
      case '尿失禁评估':
        return {
          question: '膀胱健康评估：评估您的膀胱控制能力和相关症状',
          answer: details || '用户完成了膀胱健康评估'
        };

      // 照护者支持评估
      case '照护者支持评估':
        return {
          question: '照护者支持评估：评估照护者的支持需求和负担情况',
          answer: details || '用户完成了照护者支持评估'
        };

      // 英文测试名称处理
      case 'hearing test':
        if (responses?.hearingTests) {
          const hearingTests = responses.hearingTests;
          const answers = [];
          if (hearingTests.whisperTest) {
            answers.push(`耳语测试 - 左耳：${hearingTests.whisperTest.leftEar}，右耳：${hearingTests.whisperTest.rightEar}`);
          }
          if (hearingTests.backgroundNoise) {
            answers.push(`背景噪音下对话：${hearingTests.backgroundNoise}`);
          }
          if (hearingTests.conversationDifficulty) {
            answers.push(`日常对话困难：${hearingTests.conversationDifficulty}`);
          }
          return {
            question: '听力测试：评估您在安静环境中的听力能力',
            answer: answers.length > 0 ? answers.join('；') : '听力测试已完成'
          };
        }
        return {
          question: '听力测试：评估您在安静环境中的听力能力',
          answer: responses?.hearingTest || details || '听力测试已完成'
        };

      case 'mobility test':
        return {
          question: '行动能力测试：评估您的身体机能和活动能力',
          answer: responses?.mobilityTest || details || '行动能力测试已完成'
        };

      case 'vision test':
        if (responses?.visionTests) {
          const visionTests = responses.visionTests;
          const answers = [];
          if (visionTests.distanceVision) {
            answers.push(`远距离视力：${visionTests.distanceVision}`);
          }
          if (visionTests.nearVision) {
            answers.push(`近距离视力：${visionTests.nearVision}`);
          }
          if (visionTests.contrastSensitivity) {
            answers.push(`对比敏感度：${visionTests.contrastSensitivity}`);
          }
          if (visionTests.glareTest) {
            answers.push(`眩光测试：${visionTests.glareTest}`);
          }
          return {
            question: '视力测试：评估您的远视力和近视力能力',
            answer: answers.length > 0 ? answers.join('；') : '视力测试已完成'
          };
        }
        return {
          question: '视力测试：评估您的远视力和近视力能力',
          answer: responses?.visionTest || details || '视力测试已完成'
        };

      case 'psychological test':
      case 'phq-2':
        return {
          question: 'PHQ-2心理健康评估：评估您最近的情绪状态和心理健康',
          answer: responses?.psychologicalTest || details || '心理健康评估已完成'
        };

      case 'nutrition test':
      case 'vitality test':
        return {
          question: '营养活力评估：评估您的营养状况和身体活力',
          answer: responses?.vitalityTest || details || '营养活力评估已完成'
        };

      default:
        // 使用增强的中文映射
        const chineseName = getChineseTestName(testName);
        return {
          question: chineseName.includes('?') || chineseName.includes('？') ? chineseName : `${chineseName}评估`,
          answer: details || '评估已完成'
        };
    }
  };


  // 获取领域配置
  const getDomainConfig = (domainKey: string) => {
    const domainConfigMap: Record<string, { name: string }> = {
      cognition: { name: '认知能力' },
      mobility: { name: '行动能力' },
      vitality: { name: '营养状况' },
      vision: { name: '视力' },
      hearing: { name: '听力' },
      psychologicalCapacity: { name: '心理健康' },
      socialCareSupport: { name: '社会照护支持' },
      urinaryIncontinence: { name: '尿失禁' },
      carerSupport: { name: '照护者支持' }
    };
    return domainConfigMap[domainKey] || { name: '未知领域' };
  };

  // 获取评估详情数据
  const getAssessmentDetails = (domainKey: string) => {

    // ✅ 修复：优先使用 activeAssessmentResult 而不是 assessmentResult
    const currentAssessmentResult = activeAssessmentResult;

    if (!currentAssessmentResult) {
      console.warn('⚠️ currentAssessmentResult 不存在，无法获取评估详情');
      console.warn('🔍 Debug info:', {
        hasActiveResult: !!activeAssessmentResult,
        hasPropResult: !!assessmentResult,
        hasLoadedResult: !!loadedAssessmentResult,
        activeAssessmentResultKeys: activeAssessmentResult ? Object.keys(activeAssessmentResult) : 'no-active',
        urlAssessmentId: urlAssessmentId
      });
      return null;
    }
    
    if (!domainKey) {
      console.warn('⚠️ domainKey 为空，无法获取评估详情');
      return null;
    }

    try {
      // 内在能力评估详情
      const intrinsicResult = currentAssessmentResult.intrinsicCapacity?.[domainKey as keyof typeof currentAssessmentResult.intrinsicCapacity];
      if (intrinsicResult) {
        // 从testResults中提取rawUserAnswers作为responses数据
        const extractResponses = () => {
          if (intrinsicResult.testResults && intrinsicResult.testResults.length > 0) {
            // 如果有多个测试，合并所有的rawUserAnswers
            const allResponses: any = {};
            intrinsicResult.testResults.forEach((test: any) => {
              if (test.rawUserAnswers) {
                Object.assign(allResponses, test.rawUserAnswers);
              }
            });
            
            // 为所有评估域保持testResults结构，以便Summary组件多路径访问
            allResponses.testResults = intrinsicResult.testResults;
            
            return Object.keys(allResponses).length > 1 ? allResponses : null; // >1 因为至少有testResults
          }
          return (intrinsicResult as any).responses || null;
        };

        return {
          name: getDomainConfig(domainKey).name,
          status: intrinsicResult.status,
          completionMethod: intrinsicResult.completionMethod,
          filterQuestionResult: intrinsicResult.filterQuestionResult,
          testResults: Array.isArray(intrinsicResult.testResults) ? intrinsicResult.testResults : [],
          responses: extractResponses(),
          domainKey: domainKey,
          type: 'intrinsic'
        };
      }
      
      // 关键因素评估详情
      const keyFactorResult = currentAssessmentResult.keyFactors?.[domainKey as keyof typeof currentAssessmentResult.keyFactors];
      if (keyFactorResult) {
        
        const details: any = {
          name: getDomainConfig(domainKey).name,
          status: keyFactorResult.status,
          responses: keyFactorResult.responses || keyFactorResult,
          domainKey: domainKey,
          type: 'keyFactor'
        };
        
        // 特殊处理不同关键因素的特定字段
        if (domainKey === 'urinaryIncontinence') {
          details.hasProblem = (keyFactorResult as any).hasProblem;
        } else if (domainKey === 'socialCareSupport') {
          details.riskAreas = (keyFactorResult as any).riskAreas;
        } else if (domainKey === 'carerSupport') {
          details.supportNeedsMet = (keyFactorResult as any).supportNeedsMet;
          details.hasConfidence = (keyFactorResult as any).hasConfidence;
          details.negativeImpacts = (keyFactorResult as any).negativeImpacts;
        }
        
        return details;
      }
      
      console.warn(`⚠️ 未找到 ${domainKey} 的评估数据`);
      return null;
    } catch (error) {
      console.error('❌ 获取评估详情时发生错误:', error);
      return null;
    }
  };

  // 生成个性化内容 - 使用统一的用户信息
  const personalizedContent = useMemo(() => {
    if (!activeAssessmentResult) {
      console.warn('⚠️ 评估结果为空，无法生成个性化内容');
      return null;
    }

    try {
      // 使用userDisplayInfo作为用户档案
      const content = generatePersonalizedContent(activeAssessmentResult, userDisplayInfo);
      console.log(`✅ [EnhancedResultsPage] 个性化内容生成成功 (数据源: ${userDisplayInfo.source})`);
      return content;
    } catch (error) {
      console.error('❌ 生成个性化内容时出错:', error);
      // 返回基础的内容结构，确保界面不会崩溃
      return {
        universalAdvice: [],
        specificAdvice: [],
        overallSummary: {
          totalAssessments: 0,
          passedAssessments: 0,
          failedAssessments: 0,
          overallStatus: activeAssessmentResult?.overallStatus || 'UNKNOWN',  // ✅ 修复null访问
          priorityRecommendations: [],
          nextSteps: ["请重新刷新页面"]
        }
      };
    }
  }, [activeAssessmentResult, userDisplayInfo]);

  // 当 personalizedContent 生成后，初始化 specificAdviceList
  useEffect(() => {
    if (personalizedContent) {
      setSpecificAdviceList(personalizedContent.specificAdvice);
    }
  }, [personalizedContent]);

  // "优先处理" 按钮的点击处理函数
  const handlePrioritize = (adviceId: string) => {
    setSpecificAdviceList(prevList => {
      const itemToMove = prevList.find(item => item.id === adviceId);
      if (!itemToMove) return prevList;

      // 切换状态并创建新列表
      const newList = prevList.map(item => ({
        ...item,
        isPrioritized: item.id === adviceId ? !item.isPrioritized : item.isPrioritized
      }));

      // 排序：优先项在前，其他项保持原顺序
      return newList.sort((a, b) => {
        if (a.isPrioritized && !b.isPrioritized) return -1;
        if (!a.isPrioritized && b.isPrioritized) return 1;
        return 0;
      });
    });
  };

  // 显示加载状态
  if (loadingAssessment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">正在加载评估报告...</h2>
          <p className="text-gray-600">请稍候</p>
        </div>
      </div>
    );
  }

  // 显示错误状态
  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">加载失败</h2>
          <p className="text-gray-600 mb-6">{loadError}</p>
          <div className="space-x-4">
            <button
              onClick={() => navigate('/user-selection')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              返回用户选择
            </button>
            <button
              onClick={() => window.location.reload()}
              className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300"
            >
              重新加载
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 显示无数据状态
  if (!activeAssessmentResult || !personalizedContent) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center"><h2 className="text-2xl font-bold mb-4">暂无评估结果</h2><button onClick={() => navigate('/assessment-choice')} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700">开始评估</button></div>
      </div>
    );
  }

  const { universalAdvice, overallSummary } = personalizedContent;

  // ... (getOverallStatusInfo, getAdviceIcon, formatContentWithBullets and other helpers remain the same)
  const formatContentWithBullets = (content: string) => {
    if (!content) return { hasLists: false, formattedContent: content };
    const hasListItems = content.includes(' - ') || content.includes('- ');
    if (!hasListItems) return { hasLists: false, formattedContent: content };
    const parts = content.split(/(?=\s*-\s)/);
    const processedParts = parts.map(part => {
        const trimmedPart = part.trim();
        if (trimmedPart.startsWith('- ') || trimmedPart.startsWith(' - ')) {
            return { type: 'listItem', content: trimmedPart.replace(/^\s*-\s*/, '').trim() };
        }
        return { type: 'paragraph', content: trimmedPart.replace(/：\s*$/, '：').trim() };
    }).filter(p => p.content);
    return { hasLists: true, processedParts };
  };

  const getOverallStatusInfo = () => {
    // ✅ 使用activeAssessmentResult并添加安全检查
    const status = activeAssessmentResult?.overallStatus;
    switch (status) {
      case 'ALL_PASS': return { title: '恭喜！您的内在能力表现良好', color: 'text-green-600', bgColor: 'bg-green-50', icon: CheckCircle };
      case 'SOME_FAIL': return { title: '您的内在能力需要一些关注', color: 'text-yellow-600', bgColor: 'bg-yellow-50', icon: Clock };
      case 'MULTIPLE_FAIL': return { title: '建议关注您的内在能力状况', color: 'text-red-600', bgColor: 'bg-red-50', icon: AlertTriangle };
      default: return { title: '评估已完成', color: 'text-gray-600', bgColor: 'bg-gray-50', icon: FileText };
    }
  };
  const overallInfo = getOverallStatusInfo();
  const StatusIcon = overallInfo.icon;

  const getAdviceIcon = (adviceNumber: number) => {
    const iconMap: { [key: number]: { icon: React.ElementType, color: string, bgColor: string, borderColor: string } } = {
        1: { icon: Activity, color: 'text-emerald-600', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-200' },
        2: { icon: Apple, color: 'text-green-600', bgColor: 'bg-green-50', borderColor: 'border-green-200' },
        3: { icon: Heart, color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
        4: { icon: Smile, color: 'text-pink-600', bgColor: 'bg-pink-50', borderColor: 'border-pink-200' },
        5: { icon: Users, color: 'text-purple-600', bgColor: 'bg-purple-50', borderColor: 'border-purple-200' },
        6: { icon: Shield, color: 'text-red-600', bgColor: 'bg-red-50', borderColor: 'border-red-200' },
        7: { icon: Shield, color: 'text-indigo-600', bgColor: 'bg-indigo-50', borderColor: 'border-indigo-200' },
        8: { icon: Heart, color: 'text-rose-600', bgColor: 'bg-rose-50', borderColor: 'border-rose-200' },
        9: { icon: Clock, color: 'text-violet-600', bgColor: 'bg-violet-50', borderColor: 'border-violet-200' },
        10: { icon: Brain, color: 'text-amber-600', bgColor: 'bg-amber-50', borderColor: 'border-amber-200' },
        11: { icon: Eye, color: 'text-cyan-600', bgColor: 'bg-cyan-50', borderColor: 'border-cyan-200' },
        12: { icon: Ear, color: 'text-teal-600', bgColor: 'bg-teal-50', borderColor: 'border-teal-200' }
    };
    return iconMap[adviceNumber] || iconMap[1];
  };

  // 获取维度的图标
  const getDomainIcon = (domain: string) => {
    const icons: { [key: string]: React.ElementType } = {
      'cognition': Brain,
      'mobility': Activity,
      'vitality': Apple,
      'vision': Eye,
      'hearing': Ear,
      'psychologicalCapacity': Smile,
      'socialCareSupport': Users,
      'carerSupport': Heart,
      'urinaryIncontinence': Shield
    };
    return icons[domain] || Activity;
  };

  // 获取维度的解释说明
  const getDimensionExplanation = (dimensionLabel: string, isHealthy: boolean) => {
    const explanations: { [key: string]: { healthy: string, concern: string } } = {
      '认知': {
        healthy: '您的认知能力表现良好，思维清晰，记忆力正常。继续保持学习和思考习惯。',
        concern: '认知能力需要关注，建议进行认知训练，多进行阅读、学习等脑力活动。'
      },
      '行动': {
        healthy: '您的行动能力表现良好，身体灵活性和平衡性都不错。保持适度运动习惯。',
        concern: '行动能力需要加强，建议在安全的环境下适度锻炼，提高身体的协调性和力量。'
      },
      '营养': {
        healthy: '您的营养状况良好，体重和食欲都正常。继续保持均衡饮食习惯。',
        concern: '营养状况需要改善，注意饮食均衡，如有必要可咨询营养师制定饮食计划。'
      },
      '视力': {
        healthy: '您的视力状况良好，眼部健康状况正常。定期检查以预防视力问题。',
        concern: '视力需要关注，建议定期眼科检查，必要时配戴合适的眼镜。'
      },
      '听力': {
        healthy: '您的听力状况良好，听觉功能正常。注意保护听力，避免长时间噪声暴露。',
        concern: '听力需要关注，建议进行听力检查，必要时考虑使用助听设备。'
      },
      '心理': {
        healthy: '您的心理状况良好，情绪稳定积极。继续保持乐观的生活态度。',
        concern: '心理健康需要关注，建议多参与社交活动，必要时寻求专业心理支持。'
      }
    };
    
    const explanation = explanations[dimensionLabel];
    return explanation ? (isHealthy ? explanation.healthy : explanation.concern) : '状况评估已完成，请关注相关建议。';
  };

  const getPersonalizedAdviceStyle = (domain: string, category: 'fail' | 'pass') => {
    const domainStyleMap: { [key: string]: any } = {
      cognition: { icon: Brain, name: '认知能力', fail: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800', iconBg: 'bg-orange-100', iconText: 'text-orange-600', bullet: 'bg-orange-500' }, pass: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', iconBg: 'bg-green-100', iconText: 'text-green-600', bullet: 'bg-green-500' } },
      mobility: { icon: Activity, name: '行动能力', fail: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', iconBg: 'bg-red-100', iconText: 'text-red-600', bullet: 'bg-red-500' }, pass: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', iconBg: 'bg-green-100', iconText: 'text-green-600', bullet: 'bg-green-500' } },
      vitality: { icon: Apple, name: '营养状况', fail: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800', iconBg: 'bg-yellow-100', iconText: 'text-yellow-600', bullet: 'bg-yellow-500' }, pass: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', iconBg: 'bg-green-100', iconText: 'text-green-600', bullet: 'bg-green-500' } },
      vision: { icon: Eye, name: '视力', fail: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800', iconBg: 'bg-purple-100', iconText: 'text-purple-600', bullet: 'bg-purple-500' }, pass: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', iconBg: 'bg-green-100', iconText: 'text-green-600', bullet: 'bg-green-500' } },
      hearing: { icon: Ear, name: '听力', fail: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-800', iconBg: 'bg-indigo-100', iconText: 'text-indigo-600', bullet: 'bg-indigo-500' }, pass: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', iconBg: 'bg-green-100', iconText: 'text-green-600', bullet: 'bg-green-500' } },
      psychologicalCapacity: { icon: Smile, name: '心理健康', fail: { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-800', iconBg: 'bg-pink-100', iconText: 'text-pink-600', bullet: 'bg-pink-500' }, pass: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', iconBg: 'bg-green-100', iconText: 'text-green-600', bullet: 'bg-green-500' } },
      socialCareSupport: { icon: Users, name: '社会照护支持', fail: { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-800', iconBg: 'bg-cyan-100', iconText: 'text-cyan-600', bullet: 'bg-cyan-500' }, pass: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', iconBg: 'bg-green-100', iconText: 'text-green-600', bullet: 'bg-green-500' } },
      carerSupport: { icon: Heart, name: '照护者支持', fail: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-800', iconBg: 'bg-rose-100', iconText: 'text-rose-600', bullet: 'bg-rose-500' }, pass: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', iconBg: 'bg-green-100', iconText: 'text-green-600', bullet: 'bg-green-500' } },
      urinaryIncontinence: { icon: Shield, name: '尿失禁', fail: { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-800', iconBg: 'bg-gray-200', iconText: 'text-gray-600', bullet: 'bg-gray-500' }, pass: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', iconBg: 'bg-green-100', iconText: 'text-green-600', bullet: 'bg-green-500' } },
    };
    const domainConfig = domainStyleMap[domain] || domainStyleMap.cognition;
    return {
      icon: domainConfig.icon,
      name: domainConfig.name,
      ...domainConfig[category]
    };
  };

  // 渲染个性化建议
  const renderSpecificAdvice = () => (
    <div className="space-y-8">
      <div className="text-center bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 rounded-2xl p-8 border border-purple-100">
        <h3 className="text-2xl font-bold text-gray-800 mb-3">个性化健康建议</h3>
        <p className="text-gray-600 text-lg">基于您的评估结果，为您量身定制的健康改善建议</p>
      </div>
      
      <div className="space-y-6">
        {specificAdviceList.map((advice) => {
          const domain = advice.id.replace(/^(fail_|pass_)/, '');
          const styleConfig = getPersonalizedAdviceStyle(domain, advice.category);
          return (
            <SpecificAdviceCard
              key={advice.id}
              advice={advice}
              icon={styleConfig.icon}
              colorConfig={styleConfig}
              onPrioritize={handlePrioritize}
            />
          );
        })}
      </div>
    </div>
  );




  // ... (renderUniversalAdvice, renderSummary, and the main return structure remain the same)
  const renderUniversalAdvice = () => {
    if (!universalAdvice || universalAdvice.length === 0) {
      return <div className="text-center py-12"><div className="text-gray-500 text-lg">建议内容正在加载中...</div></div>;
    }
    return (
      <div className="space-y-8">
        <div className="text-center bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-2xl p-8 border border-blue-100">
          <h3 className="text-2xl font-bold text-gray-800 mb-3">健康生活·温馨指南</h3>
          <p className="text-gray-600 text-lg">基于世界卫生组织ICOPE手册，为您精心准备的{universalAdvice.length}项健康建议</p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {universalAdvice.map((advice) => {
            const iconConfig = getAdviceIcon(advice.adviceNumber);
            const IconComponent = iconConfig.icon;
            return (
              <div key={advice.id} className={`${iconConfig.bgColor} ${iconConfig.borderColor} border-2 rounded-2xl p-6 flex flex-col min-h-[450px] hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group cursor-pointer`}>
                <div className="flex items-start justify-between mb-4">
                  <div className={`${iconConfig.color} bg-white rounded-full p-3 shadow-md group-hover:shadow-lg group-hover:scale-110 transition-all duration-300`}>
                    <IconComponent className="w-6 h-6" />
                  </div>
                  <div className={`${iconConfig.color} bg-white rounded-full px-3 py-1 text-sm font-bold shadow-md group-hover:shadow-lg transition-all duration-300`}>
                    #{advice.adviceNumber}
                  </div>
                </div>
                
                <h4 className="font-bold text-gray-800 text-xl mb-4 group-hover:text-gray-900 transition-colors">{advice.title}</h4>
                
                <div className="text-gray-700 text-base mb-6 flex-1 font-normal leading-relaxed">
                  {(() => {
                    const contentResult = formatContentWithBullets(advice.coreContent || '');
                    if (contentResult.hasLists && contentResult.processedParts) {
                      return (
                        <div className="space-y-3">
                          {contentResult.processedParts.map((part, idx) => 
                            part.type === 'listItem' ? (
                              <div key={idx} className="flex items-start space-x-3">
                                <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-2 ${iconConfig.color.replace('text-', 'bg-')} group-hover:scale-125 transition-transform duration-200`}></div>
                                <span className="text-sm leading-relaxed group-hover:text-gray-800 transition-colors">{part.content}</span>
                              </div>
                            ) : (
                              <p key={idx} className="text-sm leading-relaxed mb-2 group-hover:text-gray-800 transition-colors">{part.content}</p>
                            )
                          )}
                        </div>
                      );
                    } else {
                      return <p className="text-sm leading-relaxed group-hover:text-gray-800 transition-colors">{advice.coreContent || '正在生成个性化建议内容...'}</p>;
                    }
                  })()}
                </div>
                
                <div className="border-t border-white pt-4 mt-auto">
                  {advice.extendedReading && advice.extendedReading.length > 0 ? (
                    <button 
                      onClick={() => setModalContent({ title: advice.title, items: advice.extendedReading })} 
                      className={`${iconConfig.color} hover:bg-white hover:shadow-md font-semibold w-full text-center py-3 rounded-lg transition-all duration-200 border border-transparent hover:border-current text-sm`}
                    >
                      📖 查看延伸阅读 ({advice.extendedReading.length}项)
                    </button>
                  ) : (
                    <div className="text-center py-3">
                      <span className="text-gray-400 text-xs bg-white px-3 py-1 rounded-full">更多内容即将添加</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // 生成智能健康画像
  const generateHealthProfile = () => {
    // ✅ 添加null检查，使用activeAssessmentResult而不是assessmentResult
    if (!activeAssessmentResult) {
      return {
        passedDomains: [],
        failedDomains: [],
        passedFactors: [],
        failedFactors: [],
        overallStrength: 0,
        overallConcerns: 0,
        totalAssessed: 0
      };
    }

    const domains = ['cognition', 'mobility', 'vitality', 'vision', 'hearing', 'psychologicalCapacity'];
    const keyFactors = ['socialCareSupport', 'carerSupport', 'urinaryIncontinence'];

    const passedDomains = domains.filter(d => activeAssessmentResult.intrinsicCapacity?.[d as keyof typeof activeAssessmentResult.intrinsicCapacity]?.status === 'PASS');
    const failedDomains = domains.filter(d => activeAssessmentResult.intrinsicCapacity?.[d as keyof typeof activeAssessmentResult.intrinsicCapacity]?.status === 'FAIL');

    const passedFactors = keyFactors.filter(f => {
      const result = activeAssessmentResult.keyFactors?.[f as keyof typeof activeAssessmentResult.keyFactors];
      if (f === 'urinaryIncontinence') {
        return result?.status === 'OK' || result?.status === 'CONTINENT';
      }
      return result?.status === 'SUPPORTED' || result?.status === 'OK';
    });
    const failedFactors = keyFactors.filter(f => {
      const result = activeAssessmentResult.keyFactors?.[f as keyof typeof activeAssessmentResult.keyFactors];
      if (f === 'urinaryIncontinence') {
        return result?.status === 'NEEDS_ATTENTION' || result?.status === 'INCONTINENT';
      }
      return result?.status === 'NEEDS_ATTENTION' || result?.status === 'AT_RISK';
    });

    const domainNames: Record<string, string> = {
      cognition: '认知能力', mobility: '行动能力', vitality: '营养状况',
      vision: '视力', hearing: '听力', psychologicalCapacity: '心理健康',
      socialCareSupport: '社会照护支持', carerSupport: '照护者支持', urinaryIncontinence: '尿失禁'
    };

    return {
      passedDomains: passedDomains.map(d => domainNames[d]),
      failedDomains: failedDomains.map(d => domainNames[d]),
      passedFactors: passedFactors.map(f => domainNames[f]),
      failedFactors: failedFactors.map(f => domainNames[f]),
      overallStrength: passedDomains.length + passedFactors.length,
      overallConcerns: failedDomains.length + failedFactors.length,
      totalAssessed: domains.length + keyFactors.length
    };
  };

  const healthProfile = generateHealthProfile();

  // 健康雷达图组件
  const HealthRadarChart: React.FC<{ data: { label: string, value: number, status: string }[] }> = ({ data }) => {
    const size = 280;
    const center = size / 2;
    const radius = size / 2 - 40;
    const levels = 5;
    
    // 生成多边形的点
    const getPolygonPoints = (values: number[]) => {
      return values.map((value, index) => {
        const angle = (index * 2 * Math.PI) / values.length - Math.PI / 2;
        const r = (value * radius) / 100;
        const x = center + r * Math.cos(angle);
        const y = center + r * Math.sin(angle);
        return `${x},${y}`;
      }).join(' ');
    };
    
    // 生成标签位置
    const getLabelPosition = (index: number, total: number) => {
      const angle = (index * 2 * Math.PI) / total - Math.PI / 2;
      const labelRadius = radius + 25;
      const x = center + labelRadius * Math.cos(angle);
      const y = center + labelRadius * Math.sin(angle);
      return { x, y, angle };
    };

    return (
      <div className="space-y-4">
        {/* ICOPE内在能力说明 */}
        <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
          <div className="text-center space-y-2">
            <h5 className="font-semibold text-gray-800 text-lg">关于内在能力</h5>
            <p className="text-base text-gray-600 leading-relaxed">
              内在能力是个人可以利用的所有身体和精神能力的总和。优化内在能力是健康老龄化的核心，
              通过及时发现并管理内在能力的变化，可以维持功能能力，实现更好的生活质量。
            </p>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <h4 className="text-lg font-bold text-gray-800 mb-4 text-center">内在能力雷达图</h4>
        <div className="flex justify-center">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            {/* 背景同心圆 */}
            {Array.from({ length: levels }, (_, i) => (
              <circle
                key={i}
                cx={center}
                cy={center}
                r={(radius * (i + 1)) / levels}
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="1"
                opacity={0.6}
              />
            ))}
            
            {/* 背景线条 */}
            {data.map((_, index) => {
              const angle = (index * 2 * Math.PI) / data.length - Math.PI / 2;
              const x = center + radius * Math.cos(angle);
              const y = center + radius * Math.sin(angle);
              return (
                <line
                  key={index}
                  x1={center}
                  y1={center}
                  x2={x}
                  y2={y}
                  stroke="#e5e7eb"
                  strokeWidth="1"
                  opacity={0.6}
                />
              );
            })}
            
            {/* 数据多边形 */}
            <polygon
              points={getPolygonPoints(data.map(d => d.value))}
              fill="rgba(59, 130, 246, 0.2)"
              stroke="#3b82f6"
              strokeWidth="2"
            />
            
            {/* 数据点 */}
            {data.map((item, index) => {
              const angle = (index * 2 * Math.PI) / data.length - Math.PI / 2;
              const r = (item.value * radius) / 100;
              const x = center + r * Math.cos(angle);
              const y = center + r * Math.sin(angle);
              const isHealthy = item.status === 'PASS' || item.status === 'SUPPORTED' || item.status === 'OK' || item.status === 'CONTINENT';
              
              return (
                <circle
                  key={index}
                  cx={x}
                  cy={y}
                  r="6"
                  fill={isHealthy ? "#10b981" : "#ef4444"}
                  stroke="white"
                  strokeWidth="2"
                />
              );
            })}
            
            {/* 标签 */}
            {data.map((item, index) => {
              const { x, y } = getLabelPosition(index, data.length);
              const isHealthy = item.status === 'PASS' || item.status === 'SUPPORTED' || item.status === 'OK' || item.status === 'CONTINENT';
              
              return (
                <g key={index}>
                  <text
                    x={x}
                    y={y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className={`text-xs font-medium ${isHealthy ? 'fill-green-700' : 'fill-red-700'}`}
                  >
                    {item.label}
                  </text>
                </g>
              );
            })}
            
            {/* 中心点 */}
            <circle cx={center} cy={center} r="3" fill="#6b7280" />
          </svg>
        </div>
        
          {/* 图例 */}
          <div className="flex justify-center mt-4 space-x-6">
            <div className="flex items-center">
              <div className="w-4 h-4 rounded-full bg-green-500 mr-2"></div>
              <span className="text-sm text-gray-600">表现良好</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 rounded-full bg-red-500 mr-2"></div>
              <span className="text-sm text-gray-600">需要关注</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 准备雷达图数据
  const getRadarData = () => {
    const domains = [
      { key: 'cognition', label: '认知', name: '认知能力' },
      { key: 'mobility', label: '行动', name: '行动能力' },
      { key: 'vitality', label: '营养', name: '营养状况' },
      { key: 'vision', label: '视力', name: '视力' },
      { key: 'hearing', label: '听力', name: '听力' },
      { key: 'psychologicalCapacity', label: '心理', name: '心理健康' },
    ];

    // ✅ 添加null检查，使用activeAssessmentResult
    if (!activeAssessmentResult || !activeAssessmentResult.intrinsicCapacity) {
      return domains.map(domain => ({
        label: domain.label,
        name: domain.name,
        value: 60,
        status: 'NOT_TESTED'
      }));
    }

    return domains.map(domain => {
      const result = activeAssessmentResult.intrinsicCapacity[domain.key as keyof typeof activeAssessmentResult.intrinsicCapacity];
      const status = result?.status || 'NOT_TESTED';
      const value = status === 'PASS' ? 85 : status === 'FAIL' ? 35 : 60;
      
      return {
        label: domain.label,
        name: domain.name,
        value,
        status
      };
    });
  };

  const radarData = getRadarData();

  // 生成个性化状况描述
  const generatePersonalizedDescription = () => {
    const { overallStrength, overallConcerns, failedDomains, passedDomains } = healthProfile;
    
    if (overallConcerns === 0) {
      return `您在所有评估项目中都表现良好，展现了良好的内在能力和健康状况。特别是在${passedDomains.slice(0, 3).join('、')}等方面表现突出。继续保持这种良好的状态，定期进行健康评估即可。`;
    } else if (overallConcerns <= 2) {
      return `您的整体健康状况较好，在${passedDomains.length}个方面表现良好。需要重点关注${failedDomains.join('、')}方面，通过针对性的改善措施，您的健康状况有很大改善空间。`;
    } else {
      return `评估显示您在多个健康领域需要关注，包括${failedDomains.slice(0, 3).join('、')}等。但请不要担心，这些问题大多可以通过合理的干预措施得到改善。建议优先处理最影响日常生活的问题。`;
    }
  };

  // 分析问题关联性
  const analyzeCorrelations = () => {
    const correlations = [];
    const { failedDomains } = healthProfile;
    
    if (failedDomains.includes('认知能力') && failedDomains.includes('心理健康')) {
      correlations.push('认知能力下降往往与心理健康问题相关联，改善情绪状态有助于认知功能恢复');
    }
    if (failedDomains.includes('行动能力') && failedDomains.includes('营养状况')) {
      correlations.push('行动能力限制可能影响营养摄入，而营养不良又会进一步削弱体力');
    }
    if (failedDomains.includes('视力') && failedDomains.includes('行动能力')) {
      correlations.push('视力问题增加跌倒风险，影响日常活动能力，需要同时关注视力矫正和环境安全');
    }
    if (failedDomains.includes('听力') && failedDomains.includes('认知能力')) {
      correlations.push('听力损失可能导致社交减少，进而影响认知功能，及时使用助听设备很重要');
    }
    
    return correlations;
  };

  const renderSummary = () => {
    // ✅ 添加null检查 - 如果评估结果为空，显示加载状态
    if (!activeAssessmentResult) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">正在加载评估数据...</p>
          </div>
        </div>
      );
    }

    const correlations = analyzeCorrelations();

    // 处理功能开发中的点击事件（局部函数）
    const handleFeatureInDevelopment = async (featureName: string) => {
      // 简洁的用户提示
      toast.info('功能开发中', `${featureName}功能正在建设中，敬请期待。`);

      // 发送点击统计到后端服务器
      try {
        // 使用UserContext提供的统一用户信息
        const effectiveUserId = currentUser?.id || assessmentResult?.userId || 'unknown_user';

        const clickData = {
          feature: featureName,
          // 记录被评估用户ID和照护者ID
          userId: effectiveUserId,
          carerId: carer?.id || 'unknown_carer',
          userName: getUserDisplayName(),
          carerName: carer?.name || '匿名照护者',
          userAgent: navigator.userAgent,
          screenResolution: `${window.screen.width}x${window.screen.height}`
        };

        // 🔧 修复：使用apiConfigManager构建正确的API URL + 使用authService获取认证头
        const clickUrl = apiConfigManager.buildApiUrl('/feature_clicks');
        const authHeaders = authService.getAuthHeaders();

        const response = await fetch(clickUrl, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ click: clickData })
        });

        if (!response.ok) {
          console.warn('Failed to record feature click:', response.status);
        }
      } catch (error) {
        // 静默失败，不影响用户体验
        console.warn('Error recording feature click:', error);
      }
    };

    return (
      <div className="space-y-8">
        {/* 第一层：内在能力全景图 */}
        <div className="bg-white rounded-2xl p-6 lg:p-8 border border-gray-200 shadow-sm">
          <h3 className="text-xl lg:text-2xl font-bold text-gray-800 mb-6 text-center">内在能力全景图</h3>
          
          <div className="grid lg:grid-cols-5 gap-8 items-start">
            {/* 左侧：雷达图区域 */}
            <div className="lg:col-span-2 flex flex-col items-center">
              <div className="mb-4">
                <HealthRadarChart data={radarData} />
              </div>
            </div>
            
            {/* 右侧：老年友好解读区域 */}
            <div className="lg:col-span-3">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  <span className="text-2xl mr-2">📋</span>
                  您的内在能力状态
                </h4>
                
                <div className="space-y-4">
                  {radarData.map((dimension, index) => {
                    const isHealthy = dimension.status === 'PASS';
                    const icons = ['🧠', '🚶', '🍎', '👁️', '👂', '😊'];
                    const friendlyNames = ['动脑思考', '行走活动', '营养状况', '看东西', '听声音', '心情状态'];
                    const suggestions = [
                      isHealthy ? '多读书看报' : '多动脑筋，读书看报',
                      isHealthy ? '保持运动' : '每天散散步，做做操',
                      isHealthy ? '继续保持' : '注意营养均衡',
                      isHealthy ? '定期检查' : '定期查眼睛',
                      isHealthy ? '听力正常' : '注意保护听力',
                      isHealthy ? '心情不错' : '多出门，找朋友聊天'
                    ];
                    
                    return (
                      <div key={dimension.label} className="flex items-center gap-4 p-3 bg-white rounded-lg shadow-sm">
                        <div className="text-2xl">{icons[index]}</div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-gray-800">{friendlyNames[index]}</span>
                            <div className="flex">
                              {[...Array(5)].map((_, i) => (
                                <div
                                  key={i}
                                  className={`w-2 h-2 rounded-full mx-0.5 ${
                                    i < Math.floor(dimension.value / 20) 
                                      ? (isHealthy ? 'bg-green-500' : 'bg-orange-500')
                                      : 'bg-gray-200'
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          <div className="text-sm text-gray-600">{suggestions[index]}</div>
                        </div>
                        <div className={`text-sm font-medium px-2 py-1 rounded-full ${
                          isHealthy 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {isHealthy ? '良好' : '需关注'}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
              </div>
            </div>
          </div>
        </div>
        
        {/* 第二层：响应式六维度分区结果 */}
        <div className="bg-white rounded-2xl p-4 sm:p-6 lg:p-8 border border-gray-200 shadow-sm">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 text-center">六大维度详细结果</h3>
          <p className="text-center text-gray-600 mb-6">内在能力是您健康的核心资本，代表身体和心理的"潜在储备"</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {radarData.map((dimension, index) => {
              const isHealthy = dimension.status === 'PASS';
              const DimensionIcon = getDomainIcon([
                'cognition', 'mobility', 'vitality', 'vision', 'hearing', 'psychologicalCapacity'
              ][index]);
              const domainKey = ['cognition', 'mobility', 'vitality', 'vision', 'hearing', 'psychologicalCapacity'][index];
              
              
              // 其他维度的专业化设计
              const getDimensionConfig = (domainKey: string) => {
                const configs = {
                  'cognition': {
                    tests: ['记忆测试', '时间定向', '空间定向'],
                    styles: {
                      healthy: {
                        bg: 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 hover:border-blue-300',
                        iconBg: 'bg-blue-100',
                        iconColor: 'text-blue-600',
                        textColor: 'text-blue-700'
                      },
                      concern: {
                        bg: 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 hover:border-amber-300',
                        iconBg: 'bg-amber-100',
                        iconColor: 'text-amber-600',
                        textColor: 'text-amber-700'
                      }
                    }
                  },
                  'mobility': {
                    tests: ['椅子起立测试'],
                    styles: {
                      healthy: {
                        bg: 'bg-gradient-to-br from-emerald-50 to-emerald-50 border-emerald-200 hover:border-emerald-300',
                        iconBg: 'bg-emerald-100',
                        iconColor: 'text-emerald-600',
                        textColor: 'text-emerald-700'
                      },
                      concern: {
                        bg: 'bg-gradient-to-br from-red-50 to-red-50 border-red-200 hover:border-red-300',
                        iconBg: 'bg-red-100',
                        iconColor: 'text-red-600',
                        textColor: 'text-red-700'
                      }
                    }
                  },
                  'vitality': {
                    tests: ['体重变化筛查', '食欲状况筛查'],
                    styles: {
                      healthy: {
                        bg: 'bg-gradient-to-br from-green-50 to-green-50 border-green-200 hover:border-green-300',
                        iconBg: 'bg-green-100',
                        iconColor: 'text-green-600',
                        textColor: 'text-green-700'
                      },
                      concern: {
                        bg: 'bg-gradient-to-br from-orange-50 to-orange-50 border-orange-200 hover:border-orange-300',
                        iconBg: 'bg-orange-100',
                        iconColor: 'text-orange-600',
                        textColor: 'text-orange-700'
                      }
                    }
                  },
                  'vision': {
                    tests: ['眼部外观检查', '远视力', '近视力'],
                    styles: {
                      healthy: {
                        bg: 'bg-gradient-to-br from-purple-50 to-purple-50 border-purple-200 hover:border-purple-300',
                        iconBg: 'bg-purple-100',
                        iconColor: 'text-purple-600',
                        textColor: 'text-purple-700'
                      },
                      concern: {
                        bg: 'bg-gradient-to-br from-pink-50 to-pink-50 border-pink-200 hover:border-pink-300',
                        iconBg: 'bg-pink-100',
                        iconColor: 'text-pink-600',
                        textColor: 'text-pink-700'
                      }
                    }
                  },
                  'hearing': {
                    tests: ['耳语测试'],
                    styles: {
                      healthy: {
                        bg: 'bg-gradient-to-br from-indigo-50 to-indigo-50 border-indigo-200 hover:border-indigo-300',
                        iconBg: 'bg-indigo-100',
                        iconColor: 'text-indigo-600',
                        textColor: 'text-indigo-700'
                      },
                      concern: {
                        bg: 'bg-gradient-to-br from-violet-50 to-violet-50 border-violet-200 hover:border-violet-300',
                        iconBg: 'bg-violet-100',
                        iconColor: 'text-violet-600',
                        textColor: 'text-violet-700'
                      }
                    }
                  },
                  'psychologicalCapacity': {
                    tests: ['情绪状态筛查', '兴趣乐趣筛查'],
                    styles: {
                      healthy: {
                        bg: 'bg-gradient-to-br from-cyan-50 to-cyan-50 border-cyan-200 hover:border-cyan-300',
                        iconBg: 'bg-cyan-100',
                        iconColor: 'text-cyan-600',
                        textColor: 'text-cyan-700'
                      },
                      concern: {
                        bg: 'bg-gradient-to-br from-rose-50 to-rose-50 border-rose-200 hover:border-rose-300',
                        iconBg: 'bg-rose-100',
                        iconColor: 'text-rose-600',
                        textColor: 'text-rose-700'
                      }
                    }
                  }
                };
                return configs[domainKey as keyof typeof configs] || configs.cognition;
              };

              // 检查是否通过filter question直接排除
              const getFilterStatus = (domainKey: string) => {
                const hasFilterQuestion = ['cognition', 'hearing', 'vision'].includes(domainKey);

                // ✅ 添加null检查，使用activeAssessmentResult
                if (!activeAssessmentResult || !activeAssessmentResult.intrinsicCapacity) {
                  return { hasFilterQuestion, passedFilter: true, completionMethod: 'N/A' };
                }

                // 基于实际评估数据判断筛查问题是否通过
                const domainResult = activeAssessmentResult.intrinsicCapacity[domainKey as keyof typeof activeAssessmentResult.intrinsicCapacity];
                const completionMethod = domainResult?.completionMethod;
                
                // 🎯 统一标准筛查逻辑
                let passedFilter: boolean;
                if (domainResult?.filterQuestionResult !== undefined) {
                  // 统一标准：filterQuestionResult: true = 筛查通过, false = 筛查失败
                  passedFilter = domainResult.filterQuestionResult === true;
                } else if (domainKey === 'hearing' && domainResult?.testResults) {
                  // 🎯 听力特殊推断：如果有耳语测试数据但filterQuestionResult为undefined，推断筛查通过
                  const hasWhisperTest = domainResult.testResults.some((test: any) => 
                    test.name === "左耳耳语测试" || test.name === "右耳耳语测试" || test.name === "耳语测试"
                  );
                  passedFilter = hasWhisperTest; // 有耳语测试说明筛查通过
                } else {
                  // 其他情况默认为通过（容错处理）
                  passedFilter = true;
                }
                
                return { hasFilterQuestion, passedFilter, completionMethod };
              };

              const config = getDimensionConfig(domainKey);
              const filterStatus = getFilterStatus(domainKey);
              
              if (domainKey === 'hearing') {
              }
              
              // 获取每个测试项的具体状态
              const getTestStatus = (testName: string) => {
                // ✅ 添加null检查，使用activeAssessmentResult
                if (!activeAssessmentResult || !activeAssessmentResult.intrinsicCapacity) {
                  return false;
                }

                const domainResult = activeAssessmentResult.intrinsicCapacity[domainKey as keyof typeof activeAssessmentResult.intrinsicCapacity];
                
                if (domainKey === 'vision') {
                  if (testName === '眼部外观检查') {
                    return domainResult?.externalEyeCheckPassed ?? true;
                  } else if (testName === '远视力') {
                    // 远视力通过需要：右眼和左眼的大E测试都通过
                    // 修复：从testResults数组中查找实际测试结果
                    const rightEyeTest = domainResult?.testResults?.find((t: any) =>
                      t.name === "右眼大E字符测试 (6/60)"
                    );
                    const leftEyeTest = domainResult?.testResults?.find((t: any) =>
                      t.name === "左眼大E字符测试 (6/60)"
                    );
                    const rightEyePassed = rightEyeTest?.passed ?? false;
                    const leftEyePassed = leftEyeTest?.passed ?? false;
                    return rightEyePassed && leftEyePassed;
                  } else if (testName === '近视力') {
                    // 修复：从testResults中获取近视力测试的passed状态
                    const nearVisionTest = domainResult?.testResults?.find((t: any) => t.name === "近视力测试 (N6)");
                    return nearVisionTest?.passed ?? false;
                  }
                }
                
                // 🎯 修复营养状况的具体测试项目状态
                if (domainKey === 'vitality') {
                  if (testName === '体重变化筛查') {
                    // 从testResults中查找体重测试的具体结果
                    const weightTest = domainResult?.testResults?.find((t: any) => t.name === "体重减轻评估");
                    return weightTest?.passed ?? false;
                  } else if (testName === '食欲状况筛查') {
                    // 从testResults中查找食欲测试的具体结果
                    const appetiteTest = domainResult?.testResults?.find((t: any) => t.name === "食欲减退评估");
                    return appetiteTest?.passed ?? false;
                  }
                }
                
                // 其他域使用整体状态
                return isHealthy;
              };
              
              // 如果有筛查问题且未通过，则显示为需要进一步评估
              const shouldShowConcern = !isHealthy;
              const styleSet = shouldShowConcern ? config.styles.concern : config.styles.healthy;

              return (
                <div key={dimension.label} className={`rounded-xl p-4 sm:p-6 border-2 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${styleSet.bg}`}>
                  {/* 标题区域 */}
                  <div className="flex items-center mb-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 shadow-sm ${styleSet.iconBg}`}>
                      <DimensionIcon className={`w-6 h-6 ${styleSet.iconColor}`} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-800 text-base">{dimension.name}</h4>
                      <div className={`text-sm font-medium ${styleSet.textColor}`}>
                        {shouldShowConcern ? 
                          (filterStatus.hasFilterQuestion && !filterStatus.passedFilter ? 
                            '❌ 筛查问题未通过' : 
                            '⚠️ 建议进一步评估'
                          ) : '✅ 正常'
                        }
                      </div>
                    </div>
                  </div>

                  {/* 测试表现 */}
                  <div className="mb-4">
                    <h5 className="font-medium text-gray-800 text-sm mb-2">🔍 测试表现</h5>
                    <div className="space-y-2">
                      {filterStatus.hasFilterQuestion && !filterStatus.passedFilter ? (
                        <div 
                          className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded p-1 transition-colors"
                          onClick={() => handleTestClick(domainKey)}
                        >
                          <span className="text-xs text-gray-600">筛查问题</span>
                          <span className="text-xs font-medium text-red-600">未通过</span>
                        </div>
                      ) : (
                        config.tests.map((test, idx) => {
                          const testPassed = getTestStatus(test);
                          return (
                            <div 
                              key={idx} 
                              className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded p-1 transition-colors"
                              onClick={() => handleTestClick(domainKey)}
                            >
                              <span className="text-xs text-gray-600">{test}</span>
                              <span className={`text-xs font-medium ${testPassed ? 'text-green-600' : 'text-red-600'}`}>
                                {testPassed ? '通过' : '未通过'}
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* 关键健康因素评估结果 */}
        <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
          <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">关键健康因素评估结果</h3>
          <p className="text-center text-gray-600 mb-8">这些因素与内在能力相互影响，共同决定您的功能能力</p>
          
          <div className="grid lg:grid-cols-2 gap-6">
            {/* 社会照护支持 */}
            {activeAssessmentResult.keyFactors.socialCareSupport && (
              <div className={`rounded-xl p-4 sm:p-6 border-2 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
                activeAssessmentResult.keyFactors.socialCareSupport.status === 'SUPPORTED' || activeAssessmentResult.keyFactors.socialCareSupport.status === 'OK'
                  ? 'bg-gradient-to-br from-green-50 to-green-50 border-green-200 hover:border-green-300'
                  : 'bg-gradient-to-br from-orange-50 to-orange-50 border-orange-200 hover:border-orange-300'
              }`}>
                {/* 标题区域 */}
                <div className="flex items-center mb-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 shadow-sm ${
                    activeAssessmentResult.keyFactors.socialCareSupport.status === 'SUPPORTED' || activeAssessmentResult.keyFactors.socialCareSupport.status === 'OK'
                      ? 'bg-green-100'
                      : 'bg-orange-100'
                  }`}>
                    <Users className={`w-6 h-6 ${
                      activeAssessmentResult.keyFactors.socialCareSupport.status === 'SUPPORTED' || activeAssessmentResult.keyFactors.socialCareSupport.status === 'OK'
                        ? 'text-green-600'
                        : 'text-orange-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-800 text-base">社会照护支持</h4>
                    <div className={`text-sm font-medium ${
                      activeAssessmentResult.keyFactors.socialCareSupport.status === 'SUPPORTED' || activeAssessmentResult.keyFactors.socialCareSupport.status === 'OK'
                        ? 'text-green-700'
                        : 'text-orange-700'
                    }`}>
                      {activeAssessmentResult.keyFactors.socialCareSupport.status === 'SUPPORTED' || activeAssessmentResult.keyFactors.socialCareSupport.status === 'OK'
                        ? '✅ 正常'
                        : '⚠️ 建议进一步评估'
                      }
                    </div>
                  </div>
                </div>

                {/* 测试表现 */}
                <div className="mb-4">
                  <h5 className="font-medium text-gray-800 text-sm mb-2">🔍 评估表现</h5>
                  <div className="space-y-2">
                    <div 
                      className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded p-1 transition-colors"
                      onClick={() => handleTestClick('socialCareSupport')}
                    >
                      <span className="text-xs text-gray-600">居住环境安全</span>
                      <span className={`text-xs font-medium ${
                        !activeAssessmentResult.keyFactors.socialCareSupport.responses?.home_environment ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {!activeAssessmentResult.keyFactors.socialCareSupport.responses?.home_environment ? '安全' : '有风险'}
                      </span>
                    </div>
                    <div 
                      className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded p-1 transition-colors"
                      onClick={() => handleTestClick('socialCareSupport')}
                    >
                      <span className="text-xs text-gray-600">经济状况</span>
                      <span className={`text-xs font-medium ${
                        !activeAssessmentResult.keyFactors.socialCareSupport.responses?.financial_situation ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {!activeAssessmentResult.keyFactors.socialCareSupport.responses?.financial_situation ? '稳定' : '困难'}
                      </span>
                    </div>
                    <div 
                      className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded p-1 transition-colors"
                      onClick={() => handleTestClick('socialCareSupport')}
                    >
                      <span className="text-xs text-gray-600">社会孤立</span>
                      <span className={`text-xs font-medium ${
                        !activeAssessmentResult.keyFactors.socialCareSupport.responses?.social_isolation ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {!activeAssessmentResult.keyFactors.socialCareSupport.responses?.social_isolation ? '无孤立' : '有孤立'}
                      </span>
                    </div>
                    <div 
                      className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded p-1 transition-colors"
                      onClick={() => handleTestClick('socialCareSupport')}
                    >
                      <span className="text-xs text-gray-600">社会参与</span>
                      <span className={`text-xs font-medium ${
                        !activeAssessmentResult.keyFactors.socialCareSupport.responses?.social_engagement ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {!activeAssessmentResult.keyFactors.socialCareSupport.responses?.social_engagement ? '积极' : '缺乏'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 尿失禁 */}
            {activeAssessmentResult.keyFactors.urinaryIncontinence && (
              <div className={`rounded-xl p-4 sm:p-6 border-2 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
                (activeAssessmentResult.keyFactors.urinaryIncontinence.status === 'OK' || activeAssessmentResult.keyFactors.urinaryIncontinence.status === 'CONTINENT')
                  ? 'bg-gradient-to-br from-green-50 to-green-50 border-green-200 hover:border-green-300'
                  : 'bg-gradient-to-br from-amber-50 to-amber-50 border-amber-200 hover:border-amber-300'
              }`}>
                {/* 标题区域 */}
                <div className="flex items-center mb-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 shadow-sm ${
                    (activeAssessmentResult.keyFactors.urinaryIncontinence.status === 'OK' || activeAssessmentResult.keyFactors.urinaryIncontinence.status === 'CONTINENT')
                      ? 'bg-green-100'
                      : 'bg-amber-100'
                  }`}>
                    <Shield className={`w-6 h-6 ${
                      (activeAssessmentResult.keyFactors.urinaryIncontinence.status === 'OK' || activeAssessmentResult.keyFactors.urinaryIncontinence.status === 'CONTINENT')
                        ? 'text-green-600'
                        : 'text-amber-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-800 text-base">尿失禁</h4>
                    <div className={`text-sm font-medium ${
                      (activeAssessmentResult.keyFactors.urinaryIncontinence.status === 'OK' || activeAssessmentResult.keyFactors.urinaryIncontinence.status === 'CONTINENT')
                        ? 'text-green-700'
                        : 'text-amber-700'
                    }`}>
                      {(activeAssessmentResult.keyFactors.urinaryIncontinence.status === 'OK' || activeAssessmentResult.keyFactors.urinaryIncontinence.status === 'CONTINENT')
                        ? '✅ 正常'
                        : '⚠️ 建议进一步评估'
                      }
                    </div>
                  </div>
                </div>

                {/* 测试表现 */}
                <div className="mb-4">
                  <h5 className="font-medium text-gray-800 text-sm mb-2">🔍 评估表现</h5>
                  <div className="space-y-2">
                    <div 
                      className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded p-1 transition-colors"
                      onClick={() => handleTestClick('urinaryIncontinence')}
                    >
                      <span className="text-xs text-gray-600">失禁症状</span>
                      <span className={`text-xs font-medium ${
                        !activeAssessmentResult.keyFactors.urinaryIncontinence.hasProblem ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {!activeAssessmentResult.keyFactors.urinaryIncontinence.hasProblem ? '无症状' : '有症状'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 照护者支持 */}
            {(activeAssessmentResult.keyFactors.carerSupport || process.env.NODE_ENV === 'development') && (
              <div className={`rounded-xl p-4 sm:p-6 border-2 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
                activeAssessmentResult.keyFactors.carerSupport?.status === 'SUPPORTED'
                  ? 'bg-gradient-to-br from-green-50 to-green-50 border-green-200 hover:border-green-300'
                  : 'bg-gradient-to-br from-red-50 to-red-50 border-red-200 hover:border-red-300'
              }`}>
                {/* 标题区域 */}
                <div className="flex items-center mb-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 shadow-sm ${
                    activeAssessmentResult.keyFactors.carerSupport?.status === 'SUPPORTED'
                      ? 'bg-green-100'
                      : 'bg-red-100'
                  }`}>
                    <Heart className={`w-6 h-6 ${
                      activeAssessmentResult.keyFactors.carerSupport?.status === 'SUPPORTED'
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-800 text-base">照护者支持</h4>
                    <div className={`text-sm font-medium ${
                      activeAssessmentResult.keyFactors.carerSupport?.status === 'SUPPORTED'
                        ? 'text-green-700'
                        : 'text-red-700'
                    }`}>
                      {activeAssessmentResult.keyFactors.carerSupport ? (
                        activeAssessmentResult.keyFactors.carerSupport.status === 'SUPPORTED'
                          ? '✅ 正常'
                          : '⚠️ 建议进一步评估'
                      ) : (
                        process.env.NODE_ENV === 'development' ? '🛠 数据缺失' : '⚠️ 建议进一步评估'
                      )}
                    </div>
                  </div>
                </div>

                {/* 测试表现 */}
                <div className="mb-4">
                  <h5 className="font-medium text-gray-800 text-sm mb-2">🔍 评估表现</h5>
                  <div className="space-y-2">
                    <div 
                      className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded p-1 transition-colors"
                      onClick={() => handleTestClick('carerSupport')}
                    >
                      <span className="text-xs text-gray-600">支持需求满足</span>
                      <span className={`text-xs font-medium ${
                        activeAssessmentResult.keyFactors.carerSupport?.supportNeedsMet ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {activeAssessmentResult.keyFactors.carerSupport?.supportNeedsMet ? '满足' : '不满足'}
                      </span>
                    </div>
                    <div 
                      className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded p-1 transition-colors"
                      onClick={() => handleTestClick('carerSupport')}
                    >
                      <span className="text-xs text-gray-600">照护信心</span>
                      <span className={`text-xs font-medium ${
                        activeAssessmentResult.keyFactors.carerSupport?.hasConfidence ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {activeAssessmentResult.keyFactors.carerSupport?.hasConfidence ? '有信心' : '缺乏信心'}
                      </span>
                    </div>
                    <div 
                      className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded p-1 transition-colors"
                      onClick={() => handleTestClick('carerSupport')}
                    >
                      <span className="text-xs text-gray-600">负面影响</span>
                      <span className={`text-xs font-medium ${
                        (() => {
                          const impacts = activeAssessmentResult.keyFactors.carerSupport?.negativeImpacts;
                          const hasAnyImpact = impacts?.physical || impacts?.mental || impacts?.financial || impacts?.social;
                          return !hasAnyImpact ? 'text-green-600' : 'text-red-600';
                        })()
                      }`}>
                        {(() => {
                          const impacts = activeAssessmentResult.keyFactors.carerSupport?.negativeImpacts;
                          const hasAnyImpact = impacts?.physical || impacts?.mental || impacts?.financial || impacts?.social;
                          return activeAssessmentResult.keyFactors.carerSupport ? 
                            (!hasAnyImpact ? '无影响' : '有影响') : '数据缺失';
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>


        {/* 第三层：下一步行动 */}
        <NextActionsPanel
          userId={location.state?.userId || currentUser?.id || activeAssessmentResult?.userId}
          userName={getUserDisplayName()}
          assessmentId={location.state?.assessmentId || urlAssessmentId || activeAssessmentResult?.sessionId}
        />

        {/* CareBot 开通转化入口：把 ICOPE 评估同步到 CareBot，老人扫码认领 AI 陪伴 */}
        <CareBotActivationButton
          className="mt-6"
          userId={location.state?.userId || currentUser?.id || activeAssessmentResult?.userId}
          assessmentId={location.state?.assessmentId || urlAssessmentId || activeAssessmentResult?.sessionId}
          elderName={getUserDisplayName()}
          elderPhone={location.state?.userProfile?.phone || currentUser?.phone}
        />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleGoBack}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>返回用户档案</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-xl font-bold">ICOPE内在能力评估报告</h1>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className={`${overallInfo.bgColor} border rounded-xl p-6 lg:p-8 mb-8 shadow-sm`}>
          <div className="flex flex-col lg:flex-row items-center lg:items-center gap-6 lg:gap-8">
            {/* 左侧：用户信息 + 评估状态 */}
            <div className="flex-1 flex flex-col sm:flex-row items-center lg:items-center gap-6">
              {/* 用户头像区 */}
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full flex items-center justify-center shadow-lg">
                  <User className="w-10 h-10 text-blue-600" />
                </div>
              </div>
              
              {/* 基本信息区 */}
              <div className="flex-1 text-center sm:text-left">
                <h2 className="font-bold text-gray-800 text-xl mb-3">
                  {userDisplayInfo.name}
                </h2>
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex items-center justify-center sm:justify-start gap-4">
                    <span className="flex items-center gap-1">
                      <span className="text-gray-500">年龄：</span>
                      <span className="font-medium">
                        {userDisplayInfo.age ? `${userDisplayInfo.age}岁` : '未设置'}
                      </span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="text-gray-500">性别：</span>
                      <span className="font-medium">{userDisplayInfo.gender || '未设置'}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="text-gray-500">评估时间：</span>
                      <span className="font-medium">
                        {activeAssessmentResult?.completedAt
                          ? new Date(activeAssessmentResult.completedAt).toLocaleDateString('zh-CN')
                          : '未知日期'
                        }
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* 右侧：评估结果概览 */}
            <div className="flex-shrink-0 flex items-center gap-6">
              {/* 状态指示器 */}
              <div className="flex items-center">
                <StatusIcon className={`w-14 h-14 ${overallInfo.color} flex-shrink-0`} />
              </div>
              
              {/* 数据概览 */}
              <div className="bg-white rounded-lg px-6 py-4 shadow-sm border border-gray-200 text-center">
                <div className="text-sm text-gray-600 mb-3 font-medium">
                  需关注：
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">内在能力</span>
                    <span className="text-xl font-bold text-gray-800">{healthProfile.failedDomains.length}项</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">关键因素</span>
                    <span className="text-xl font-bold text-gray-800">{healthProfile.failedFactors.length}项</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8">
              {[{ key: 'summary', label: '评估摘要' }, { key: 'universal', label: '通用建议' }, { key: 'specific', label: '个性化建议' }].map(({ key, label }) => (
                <button key={key} onClick={() => setActiveTab(key as any)} className={`py-4 px-2 border-b-2 font-medium text-sm ${activeTab === key ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{label}</button>
              ))}
            </nav>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          {activeTab === 'summary' && renderSummary()}
          {activeTab === 'universal' && renderUniversalAdvice()}
          {activeTab === 'specific' && renderSpecificAdvice()}
        </div>
      </div>
      
      {/* 延伸阅读模态窗口 */}
      {modalContent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-2xl font-bold text-gray-800">{modalContent.title} - 延伸阅读</h3>
              <button 
                onClick={() => setModalContent(null)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {modalContent.items && modalContent.items.length > 0 ? (
                <div className="space-y-6">
                  {modalContent.items.map((item: any, index: number) => (
                    <div key={item.uid || index} className="border border-gray-200 rounded-xl p-6 bg-gray-50">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h4 className="text-xl font-bold text-gray-800 mb-2">{item.title}</h4>
                          {item.summary && (
                            <p className="text-gray-600 text-sm mb-3 italic">摘要：{item.summary}</p>
                          )}
                          {item.domain && (
                            <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                              {item.domain}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="prose prose-gray max-w-none">
                        {(() => {
                          const contentResult = formatContentWithBullets(item.content || '');
                          if (contentResult.hasLists && contentResult.processedParts) {
                            return (
                              <div className="space-y-3">
                                {contentResult.processedParts.map((part: any, idx: number) => 
                                  part.type === 'listItem' ? (
                                    <div key={idx} className="flex items-start space-x-3">
                                      <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2"></div>
                                      <span className="text-gray-700 leading-relaxed">{part.content}</span>
                                    </div>
                                  ) : (
                                    <p key={idx} className="text-gray-700 leading-relaxed">{part.content}</p>
                                  )
                                )}
                              </div>
                            );
                          } else {
                            return <p className="text-gray-700 leading-relaxed">{item.content || '暂无详细内容'}</p>;
                          }
                        })()}
                      </div>
                      
                      {item.source_chapter && (
                        <div className="mt-4 pt-4 border-t border-gray-300">
                          <p className="text-sm text-gray-500">
                            来源：ICOPE手册第{item.source_chapter}章
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-500 text-lg mb-4">暂无延伸阅读内容</div>
                  <p className="text-gray-400">相关内容正在整理中，请稍后查看</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* 测试详情模态框 */}
      {selectedDomainDetails && (() => {
        try {
          const details = getAssessmentDetails(selectedDomainDetails);
          if (!details) {
            // 当无法获取详情时显示错误提示
            return (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl max-w-md w-full p-6">
                  <div className="text-center">
                    <div className="text-red-500 text-6xl mb-4">⚠️</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">数据加载异常</h3>
                    <p className="text-gray-600 mb-4">无法获取该评估项目的详细信息，请稍后重试。</p>
                    <button 
                      onClick={closeDetailsModal}
                      className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      关闭
                    </button>
                  </div>
                </div>
              </div>
            );
          }
          
          return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                {/* 模态框头部 */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-gray-900">{details.name || '未知评估'}评估详情</h3>
                    <button 
                      onClick={closeDetailsModal}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              
              {/* 模态框内容 */}
              <div className="px-6 py-4">
                
                {/* 筛查问题显示 - 使用Summary组件的正确逻辑 */}
                {details.type === 'intrinsic' && details.domainKey && getFilterQuestion(details.domainKey) && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-4">🔍 筛查问题</h4>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm font-medium text-gray-700">题目：</span>
                          <span className="text-sm text-gray-900 ml-2">
                            {getFilterQuestion(details.domainKey)}
                          </span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-700">回答：</span>
                          <span className={`text-sm ml-2 px-2 py-1 rounded ${
                            // 修复：使用正确的筛查通过判断逻辑
                            details.filterQuestionResult === true
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {/* 修复：显示正确的用户回答 */}
                            {details.filterQuestionResult === true 
                              ? `没有，我的${details.domainKey === 'cognition' ? '认知功能' : details.domainKey === 'vision' ? '视力' : '听力'}正常`
                              : `是的，我有${details.domainKey === 'cognition' ? '认知方面的' : details.domainKey === 'vision' ? '视力' : '听力'}困难`
                            }
                          </span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-700">结果：</span>
                          <span className={`text-sm ml-2 px-2 py-1 rounded ${
                            details.filterQuestionResult === true
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {details.filterQuestionResult === true ? '✓ 通过' : '✗ 未通过'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 具体测试结果 - 只在筛查问题通过时显示(或没有筛查问题的域) */}
                {details.type === 'intrinsic' && (
                  // 🎯 统一标准逻辑：filterQuestionResult应该语义一致
                  // filterQuestionResult: true = 筛查通过(无困难), false = 筛查失败(有困难)
                  (['cognition', 'vision', 'hearing'].includes(details.domainKey) 
                    ? (details.filterQuestionResult !== false)  // 只有明确为false时才不显示
                    : true)
                ) && (
                  <div className="mb-6">
                    {/* 使用统一的测试结果显示组件 */}
                    {details.domainKey === 'cognition' ? (() => {
                      return (
                        <CognitionSummaryDisplay 
                          responses={details}
                          cognitionStatus={details.status}
                          className="bg-white rounded-lg p-6"
                        />
                      );
                    })() : details.domainKey === 'hearing' ? (() => {
                      return (
                        <HearingSummaryDisplay 
                          responses={details}
                          hearingStatus={details.status}
                          className="bg-white rounded-lg p-6"
                        />
                      );
                    })() : details.domainKey === 'vision' ? (
                      <VisionSummaryDisplay 
                        responses={details}
                        visionStatus={details.status}
                        className="bg-white rounded-lg p-6"
                      />
                    ) : details.domainKey === 'mobility' ? (
                      <MobilitySummaryDisplay 
                        responses={details}
                        mobilityStatus={details.status}
                        completionMethod={details.completionMethod}
                        className="bg-white rounded-lg p-6"
                      />
                    ) : details.domainKey === 'vitality' ? (
                      <NutritionSummaryDisplay 
                        responses={details}
                        nutritionStatus={details.status}
                        className="bg-white rounded-lg p-6"
                      />
                    ) : details.domainKey === 'psychologicalCapacity' ? (
                      <PsychologicalSummaryDisplay 
                        responses={details}
                        psychologicalStatus={details.status}
                        className="bg-white rounded-lg p-6"
                      />
                    ) : (
                      <>
                        <h4 className="font-semibold text-gray-900 mb-4">📋 具体测试结果</h4>
                    
                    
                    <div className="space-y-4">
                      {/* 如果有具体的testResults，显示它们 */}
                      {details.testResults && details.testResults.length > 0 ? (
                        details.testResults
                          .filter((test: any) => !test.name.includes('筛查') && !test.name.includes('问题筛查'))
                          .map((test: any, index: number) => {
                          const questionAndAnswer = getTestQuestionAndAnswer(test.name, test.details, details.responses);
                          return (
                          <div key={index} className={`p-4 rounded-lg border-l-4 ${
                            test.passed ? 'bg-green-50 border-l-green-400 border border-green-200' : 'bg-red-50 border-l-red-400 border border-red-200'
                          }`}>
                            <div className="space-y-3">
                              {/* 测试题目 */}
                              <div>
                                <span className="text-sm font-medium text-gray-700">题目：</span>
                                <span className="text-sm text-gray-900 ml-2">
                                  {questionAndAnswer.question}
                                </span>
                              </div>
                              
                              {/* 用户答案 */}
                              <div>
                                <span className="text-sm font-medium text-gray-700">回答：</span>
                                <span className="text-sm text-gray-900 ml-2">
                                  {questionAndAnswer.answer}
                                </span>
                              </div>

                              {/* 测试结果 */}
                              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-medium text-gray-700">结果：</span>
                                  <span className={`text-sm px-2 py-1 rounded ${
                                    test.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                  }`}>
                                    {test.passed ? '✓ 通过' : '✗ 未通过'}
                                  </span>
                                  {/* 照护者修正标识 */}
                                  {test.carerCorrected && (
                                    <span className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">
                                      👥 照护者确认
                                    </span>
                                  )}
                                </div>
                                
                                {/* 额外信息 */}
                                {test.details && (
                                  <div className="text-xs text-gray-500">
                                    {test.details}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })) : (
                        /* 没有具体testResults时，创建默认的测试结果显示 */
                        <div className={`p-4 rounded-lg border-l-4 ${
                          details.status === 'PASS' ? 'bg-green-50 border-l-green-400 border border-green-200' : 'bg-red-50 border-l-red-400 border border-red-200'
                        }`}>
                          <div className="space-y-3">
                            {/* 测试题目 */}
                            <div>
                              <span className="text-sm font-medium text-gray-700">题目：</span>
                              <span className="text-sm text-gray-900 ml-2">
                                {(() => {
                                  const testName = 
                                    details.domainKey === 'mobility' ? 'mobility test' :
                                    details.domainKey === 'hearing' ? 'hearing test' :
                                    details.domainKey === 'vision' ? 'vision test' :
                                    details.domainKey === 'psychologicalCapacity' ? 'psychological test' :
                                    details.domainKey === 'vitality' ? 'vitality test' :
                                    `${details.name}评估`;
                                  
                                  return getTestQuestionAndAnswer(testName, null, details.responses).question;
                                })()}
                              </span>
                            </div>
                            
                            {/* 用户答案 */}
                            <div>
                              <span className="text-sm font-medium text-gray-700">回答：</span>
                              <span className="text-sm text-gray-900 ml-2">
                                {(() => {
                                  const testName = 
                                    details.domainKey === 'mobility' ? 'mobility test' :
                                    details.domainKey === 'hearing' ? 'hearing test' :
                                    details.domainKey === 'vision' ? 'vision test' :
                                    details.domainKey === 'psychologicalCapacity' ? 'psychological test' :
                                    details.domainKey === 'vitality' ? 'vitality test' :
                                    `${details.name}评估`;
                                  
                                  return getTestQuestionAndAnswer(testName, null, details.responses).answer;
                                })()}
                              </span>
                            </div>

                            {/* 测试结果 */}
                            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-gray-700">结果：</span>
                                <span className={`text-sm px-2 py-1 rounded ${
                                  details.status === 'PASS' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                }`}>
                                  {details.status === 'PASS' ? '✓ 通过' : '✗ 未通过'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                      </>
                    )}
                  </div>
                )}

                {/* 关键因素的具体测试结果 - 使用统一的测试结果显示组件 */}
                {details.type === 'keyFactor' && details.responses && (
                  <div className="mb-6">
                    {details.domainKey === 'socialCareSupport' ? (
                      <SocialCareTestResult 
                        responses={details.responses}
                        riskAreas={details.riskAreas || []}
                        className="bg-white rounded-lg p-6"
                      />
                    ) : details.domainKey === 'urinaryIncontinence' ? (
                      <UrinaryTestResult 
                        responses={details.responses}
                        hasProblem={details.hasProblem || false}
                        className="bg-white rounded-lg p-6"
                      />
                    ) : details.domainKey === 'carerSupport' ? (
                      <CarerSupportTestResult 
                        responses={details.responses}
                        supportNeedsMet={details.supportNeedsMet || false}
                        hasConfidence={details.hasConfidence || false}
                        negativeImpacts={details.negativeImpacts || { physical: false, mental: false, financial: false, social: false }}
                        className="bg-white rounded-lg p-6"
                      />
                    ) : (
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 mb-4">📋 具体测试结果</h4>
                        <div className="space-y-4">
                          <div className={`p-4 rounded-lg border-l-4 ${
                            details.status === 'OK' ? 'bg-green-50 border-l-green-400 border border-green-200' : 'bg-red-50 border-l-red-400 border border-red-200'
                          }`}>
                            <div className="space-y-3">
                              <div>
                                <span className="text-sm font-medium text-gray-700">评估项目：</span>
                                <span className="text-sm text-gray-900 ml-2">{details.name}</span>
                              </div>
                              <div>
                                <span className="text-sm font-medium text-gray-700">评估状态：</span>
                                <span className={`text-sm px-2 py-1 rounded ml-2 ${
                                  details.status === 'OK' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                }`}>
                                  {details.status === 'OK' ? '✓ 正常' : '⚠️ 需要关注'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                
                {/* 关闭按钮 */}
                <div className="pt-4 border-t border-gray-200">
                  <button 
                    onClick={closeDetailsModal}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    关闭
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
        } catch (error) {
          console.error('❌ 渲染测试详情模态框时发生错误:', error);
          // 发生错误时显示通用错误提示
          return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl max-w-md w-full p-6">
                <div className="text-center">
                  <div className="text-red-500 text-6xl mb-4">💥</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">系统错误</h3>
                  <p className="text-gray-600 mb-4">显示评估详情时出现问题，请关闭后重新尝试。</p>
                  <button 
                    onClick={closeDetailsModal}
                    className="bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    关闭
                  </button>
                </div>
              </div>
            </div>
          );
        }
      })()}
    </div>
  );
};

export default EnhancedResultsPage;
